import { useNavigate } from "react-router-dom";
import { Conversation } from "../api/models/Conversation";
import { ToolbarComponent } from "./toolbar-component";
import { addEdge, Background, BackgroundVariant, Controls, Handle, Node, NodeProps, NodeTypes, Position, ReactFlow, useEdgesState, useNodesState, useReactFlow } from "@xyflow/react";
import { ArrowsPointingOutIcon, ChevronLeftIcon, ChevronRightIcon, InformationCircleIcon, PencilIcon, PlayIcon, PlusCircleIcon, TrashIcon, SparklesIcon } from "@heroicons/react/24/solid";
import '@xyflow/react/dist/style.css';
import { useCallback, useEffect, useRef, useState } from "react";
import { DecisionCondition, Step, StepStatus, StepType, Workflow, WorkflowEdgeOutcome, WorkflowExecutionLog, WorkflowGraph, WorkflowGraphEdge, WorkflowTestFile } from "../api/models/Workflows";
import { Mail, Cpu, FileIcon, DatabaseIcon, Clock, CloudAlertIcon, ToggleLeftIcon, ToggleRightIcon, X, Diamond, AlertTriangleIcon, Check, ArrowLeftRight, BeakerIcon, FlaskConical, PlugIcon, CurlyBraces, CopyIcon, Zap, RefreshCwIcon, Search, FileSearch, CheckCircle2, LoaderCircle, CalendarDays } from 'lucide-react'; // Added RefreshCwIcon
import { useDispatch } from "react-redux";
import NotificationPopup from "./notification-popup-component";
import { setNotification } from "../store/notifications-reducer";
import { createStep, createWorkflow, deleteStep, deleteWorkflow, getAllWorkflows, getWorkflow, testWorkflow, updateStep, updateWorkflow, retryWorkflowExecution, getWorkflowExecutionLogsPaginated, updateWorkflowGraph, getWorkflowTestFiles } from "../api/thunks/workflows"; // Import the new thunk
import { useAppSelector } from "../store/hooks";
import { Assistant } from "../api/models/Assistant";
import { JSONSchemaInput } from "./json-schema-input";
import dayjs from 'dayjs'; // Import dayjs for date manipulation
import duration from 'dayjs/plugin/duration'; // Import duration plugin for dayjs
import { Secret } from "../api/models/Secret";
import { JMESPathAssistantPopup } from "./jmes-helper-popup";
import { getJmesPathSuggestion } from "../api/thunks/suggestions";
import { workflowTemplates } from "../sample_assets/workflow_templates";
import { SignUpPopup } from "./signup-popup";
import { StepDataInspector, StepOutputButton } from "./step-data-inspector";
import { v4 } from "uuid";
dayjs.extend(duration);

const nodeTypes = {
    email: EmailNode,
    llm: LLMNode,
    'data-source': DataSourceNode,
    'menuNode': MenuNode,
    "decision": DecisionStepNode,
    "terminal": TerminalNode,
    "workflowExecutionLogNode": WorkflowExecutionLogNode,
    'api-destination': ApiDestinationNode,
    'json-transformation': JSONTransformationNode,
    'reddit-action': RedditActionNode,
    'schedule': ScheduleNode,
}

const graphOutcomeColor = (outcome: WorkflowEdgeOutcome): string => {
    if (outcome === 'true') return '#15803d';
    if (outcome === 'false') return '#b42318';
    return '#c2400c';
}

const graphOutcomeLabel = (outcome: WorkflowEdgeOutcome): string => {
    if (outcome === 'true') return 'Yes';
    if (outcome === 'false') return 'No';
    return '';
}

const validateDecisionCondition = (condition: unknown, depth = 0): string | null => {
    if (depth > 12) return 'Logic can be nested up to 12 levels.';
    if (!condition || typeof condition !== 'object' || Array.isArray(condition)) return 'Each expression must be an object.';
    const value = condition as Record<string, unknown>;
    const hasAll = Object.prototype.hasOwnProperty.call(value, 'all');
    const hasAny = Object.prototype.hasOwnProperty.call(value, 'any');
    const hasNot = Object.prototype.hasOwnProperty.call(value, 'not');
    const hasLeaf = ['path', 'operator', 'value', 'attributeType'].some(key => Object.prototype.hasOwnProperty.call(value, key));
    if ([hasAll, hasAny, hasNot, hasLeaf].filter(Boolean).length !== 1) {
        return 'Each expression needs exactly one of all, any, not, or a comparison.';
    }
    if (hasAll || hasAny) {
        const key = hasAll ? 'all' : 'any';
        const children = value[key];
        if (!Array.isArray(children) || children.length === 0) return `${key} needs at least one expression.`;
        for (let index = 0; index < children.length; index++) {
            const error = validateDecisionCondition(children[index], depth + 1);
            if (error) return `${key}[${index}]: ${error}`;
        }
        return null;
    }
    if (hasNot) {
        const error = validateDecisionCondition(value.not, depth + 1);
        return error ? `not: ${error}` : null;
    }
    if (typeof value.path !== 'string' || !value.path.trim()) return 'A comparison needs a JMESPath path.';
    if (typeof value.operator !== 'string' || !['equal', 'not_equal', 'greater_than', 'greater_than_or_equal', 'less_than', 'less_than_or_equal', 'contains'].includes(value.operator)) {
        return 'A comparison has an unsupported operator.';
    }
    if (value.attributeType !== 'string' && value.attributeType !== 'number') return 'attributeType must be string or number.';
    if (!Object.prototype.hasOwnProperty.call(value, 'value') || value.value === null) return 'A comparison needs a non-null value.';
    if (value.attributeType === 'number' && value.operator === 'contains') return 'contains only works with strings.';
    return null;
}

const workflowGraphEdgesToFlow = (graphEdges: WorkflowGraphEdge[]): any[] => graphEdges.map(edge => ({
    id: edge.id,
    source: edge.sourceNodeId,
    target: edge.targetNodeId,
    animated: true,
    sourceHandle: edge.outcome === 'false' ? 'bottom' : edge.outcome === 'true' ? 'right' : undefined,
    label: graphOutcomeLabel(edge.outcome),
    data: { outcome: edge.outcome, inputName: edge.inputName },
    style: { stroke: graphOutcomeColor(edge.outcome), strokeWidth: 2 },
    labelStyle: { fill: graphOutcomeColor(edge.outcome), fontWeight: 700 },
}));

const layoutDAG = (steps: Step[], graphEdges: WorkflowGraphEdge[]): Record<string, { x: number, y: number }> => {
    const ids = steps.map(step => step.id!).filter(Boolean);
    const horizontalGap = steps.some(step => Boolean(step.decisionStep?.condition)) ? 610 : 410;
    const indegree = new Map(ids.map(id => [id, 0]));
    const outgoing = new Map(ids.map(id => [id, [] as string[]]));
    graphEdges.forEach(edge => {
        if (!indegree.has(edge.sourceNodeId) || !indegree.has(edge.targetNodeId)) return;
        outgoing.get(edge.sourceNodeId)!.push(edge.targetNodeId);
        indegree.set(edge.targetNodeId, (indegree.get(edge.targetNodeId) || 0) + 1);
    });

    const level = new Map<string, number>();
    const queue = ids.filter(id => indegree.get(id) === 0);
    queue.forEach(id => level.set(id, 0));
    while (queue.length > 0) {
        const source = queue.shift()!;
        for (const target of outgoing.get(source) || []) {
            level.set(target, Math.max(level.get(target) || 0, (level.get(source) || 0) + 1));
            indegree.set(target, (indegree.get(target) || 0) - 1);
            if (indegree.get(target) === 0) queue.push(target);
        }
    }

    const groups = new Map<number, string[]>();
    ids.forEach((id, index) => {
        const nodeLevel = level.get(id) ?? index;
        groups.set(nodeLevel, [...(groups.get(nodeLevel) || []), id]);
    });
    const positions: Record<string, { x: number, y: number }> = {};
    groups.forEach((nodeIDs, nodeLevel) => {
        const totalHeight = (nodeIDs.length - 1) * 360;
        nodeIDs.forEach((id, index) => {
            positions[id] = { x: nodeLevel * horizontalGap, y: 50 + index * 360 - totalHeight / 2 };
        });
    });
    return positions;
}

const remapTemplateGraphForCopy = (template: Workflow, copiedWorkflow: Workflow): WorkflowGraph | undefined => {
    if (!template.graph) return undefined;

    const templateSteps = [...template.steps].sort((left, right) => left.order - right.order);
    const copiedSteps = [...copiedWorkflow.steps].sort((left, right) => left.order - right.order);
    if (templateSteps.length !== copiedSteps.length) {
        throw new Error('The copied workflow steps do not match its template.');
    }

    const stepIDs = new Map<string, string>();
    templateSteps.forEach((step, index) => {
        if (!step.id || !copiedSteps[index].id) {
            throw new Error('A template step is missing its ID.');
        }
        stepIDs.set(step.id, copiedSteps[index].id!);
    });
    const remapStepID = (stepID: string): string => {
        const copiedStepID = stepIDs.get(stepID);
        if (!copiedStepID) throw new Error(`Template graph references an unknown step: ${stepID}`);
        return copiedStepID;
    };

    return {
        nodes: template.graph.nodes.map((node) => ({
            ...node,
            id: remapStepID(node.id),
            legacyStepId: remapStepID(node.legacyStepId || node.id),
        })),
        edges: template.graph.edges.map((edge) => ({
            ...edge,
            id: v4(),
            sourceNodeId: remapStepID(edge.sourceNodeId),
            targetNodeId: remapStepID(edge.targetNodeId),
        })),
        triggers: template.graph.triggers.map((trigger) => ({
            ...trigger,
            id: v4(),
            entryNodeId: remapStepID(trigger.entryNodeId),
        })),
    };
};

export const WorkflowsPage = ({
    handleUpdateConversation,
    workflows,
    setWorkflows,
    assistants,
    convo,
    convos,
    handleCreateConversation,
    handleDeleteConversation,
    handleDeleteShareLink,
    handleCreateShareLink,
    supportedTickers,
    userSecrets,
}: {
    handleUpdateConversation: (id: string, name: string) => void;
    workflows: Workflow[];
    assistants: Assistant[];
    setWorkflows: (w: Workflow[]) => void;
    convo: Conversation | undefined;
    convos: Conversation[];
    handleCreateConversation: (ticker: string) => void;
    supportedTickers: string[];
    handleDeleteConversation: (id: string) => Promise<void>;
    handleDeleteShareLink: (conversationId: string) => void;
    handleCreateShareLink: (conversationId: string) => void;
    userSecrets: Secret[];
}) => {
    if (window.location.pathname === '/workflows') {
        document.title = "Workflows | PocketQuant"
    }

    const [showSignUpPopup, setShowSignUpPopup] = useState(false);
    const [testFileWorkflow, setTestFileWorkflow] = useState<Workflow | null>(null);
    const user = useAppSelector((state) => state?.user);
    const dispatch = useDispatch();
    const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | undefined>(workflows.length > 0 ? workflows[0] : undefined);
    const navigate = useNavigate();
    const nodeStyle = {
        color: '#c2400c',
        borderColor: '#c2400c',
    };

    const handleConversationClick = (id: string) => {
        const queryParams = new URLSearchParams(window.location.search);
        queryParams.set("conversationId", id);
        navigate("/dashboard" + "?" + queryParams.toString());
        navigate(0);
    };

    const [nodes, setNodes, onNodesChange] = useNodesState(new Array<any>());
    const [edges, setEdges, onEdgesChange] = useEdgesState(new Array<any>());
    const [showEditWorkflowName, setShowEditWorkflowName] = useState('');
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
        return window.innerWidth < 768; // Initially collapsed on screens smaller than md breakpoint
    });

    const [showTemplateCopyIconPulse, setShowTemplateCopyIconPulse] = useState(false);
    const graphSaveRevision = useRef(0);
    const graphSaveQueue = useRef<Promise<void>>(Promise.resolve());
    const selectedWorkflowID = useRef(selectedWorkflow?.id);

    const runWorkflowTest = async (workflow: Workflow, file: WorkflowTestFile) => {
        if (!user.credentials?.accessToken) return;
        try {
            await testWorkflow(user.credentials.accessToken, workflow.id, file.id);
            dispatch(setNotification({
                type: 'info',
                message: `Testing ${workflow.name || 'workflow'} with ${file.fileName || file.s3Key}`,
            }));
        } catch (reason) {
            dispatch(setNotification({
                type: 'error',
                message: `Error testing workflow: ${reason}`,
            }));
            throw reason;
        }
    };

    const copyTemplateWorkflow = async (template: Workflow) => {
        if (!user.credentials?.accessToken) {
            dispatch(setNotification({ type: 'info', message: 'Please log in to create a workflow' }));
            setShowSignUpPopup(true);
            return;
        }

        try {
            const workflowID = await createWorkflow(user.credentials.accessToken, template.name!, template.steps);
            const copiedWorkflow = await getWorkflow(user.credentials.accessToken, workflowID);
            const templateGraph = remapTemplateGraphForCopy(template, copiedWorkflow);
            const savedGraph = templateGraph
                ? await updateWorkflowGraph(user.credentials.accessToken, workflowID, templateGraph)
                : copiedWorkflow.graph;
            const refreshedWorkflows = await getAllWorkflows(user.credentials.accessToken);
            const savedWorkflow = { ...copiedWorkflow, graph: savedGraph };

            setWorkflows(refreshedWorkflows);
            setSelectedWorkflow(refreshedWorkflows.find((workflow) => workflow.id === workflowID) || savedWorkflow);
            const queryParams = new URLSearchParams(window.location.search);
            queryParams.set('workflowId', workflowID);
            queryParams.delete('templateId');
            navigate(`/workflows?${queryParams.toString()}`, { replace: true });
        } catch (reason: any) {
            dispatch(setNotification({
                type: 'error',
                message: reason?.message || 'Failed to create workflow from template',
            }));
        }
    };

    useEffect(() => {
        selectedWorkflowID.current = selectedWorkflow?.id;
        graphSaveRevision.current++;
    }, [selectedWorkflow?.id]);

    // Listen for window resize to update sidebar state
    useEffect(() => {

        const handleResize = () => {
            // Only automatically expand on desktop, don't auto-collapse when resizing down
            if (window.innerWidth >= 768 && isSidebarCollapsed) {
                setIsSidebarCollapsed(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isSidebarCollapsed]);

    // Auto-collapse sidebar on selected workflow change on mobile
    useEffect(() => {
        if (selectedWorkflow && window.innerWidth < 768) {
            setIsSidebarCollapsed(true);
        }
    }, [selectedWorkflow]);

    const persistGraphEdges = useCallback(async (nextEdges: any[]) => {
        if (!selectedWorkflow || !user.credentials?.accessToken || selectedWorkflow.id.startsWith('wf_template')) return;

        const saveRevision = ++graphSaveRevision.current;

        const stepIDs = new Set(selectedWorkflow.steps.map(step => step.id));
        const graphEdges: WorkflowGraphEdge[] = nextEdges
            .filter(edge => stepIDs.has(edge.source) && stepIDs.has(edge.target))
            .map(edge => ({
                id: edge.id,
                sourceNodeId: edge.source,
                targetNodeId: edge.target,
                outcome: (edge.data?.outcome || 'success') as WorkflowEdgeOutcome,
                inputName: edge.data?.inputName || 'input',
            }));

        const firstStep = [...selectedWorkflow.steps].sort((left, right) => left.order - right.order)[0];
        const graph: WorkflowGraph = {
            nodes: selectedWorkflow.graph?.nodes || selectedWorkflow.steps.map(step => ({
                id: step.id!,
                legacyStepId: step.id,
                type: step.stepType,
                config: {},
            })),
            edges: graphEdges,
            triggers: selectedWorkflow.graph?.triggers || (firstStep ? [{
                id: v4(),
                entryNodeId: firstStep.id!,
                type: firstStep.stepType === 'schedule' ? 'schedule' : 's3',
                config: {},
                enabled: true,
            }] : []),
        };

        const saveRequest = graphSaveQueue.current
            .catch(() => undefined)
            .then(() => updateWorkflowGraph(user.credentials!.accessToken, selectedWorkflow.id, graph));
        graphSaveQueue.current = saveRequest.then(() => undefined, () => undefined);

        try {
            const savedGraph = await saveRequest;
            if (saveRevision !== graphSaveRevision.current || selectedWorkflowID.current !== selectedWorkflow.id) return;
            const updatedWorkflow = { ...selectedWorkflow, graph: savedGraph };
            setSelectedWorkflow(updatedWorkflow);
            setWorkflows(workflows.map(workflow => workflow.id === updatedWorkflow.id ? updatedWorkflow : workflow));
            dispatch(setNotification({ type: 'success', message: `Workflow paths saved as version ${savedGraph.version}` }));
        } catch (error: any) {
            if (saveRevision !== graphSaveRevision.current || selectedWorkflowID.current !== selectedWorkflow.id) return;
            dispatch(setNotification({ type: 'error', message: error.message || 'Failed to save workflow paths' }));
            const restoredEdges = workflowGraphEdgesToFlow(selectedWorkflow.graph?.edges || []);
            setEdges(restoredEdges);
        }
    }, [dispatch, selectedWorkflow, setEdges, setWorkflows, user.credentials?.accessToken, workflows]);

    const onConnect = useCallback((params: any) => {
        if (!params.source || !params.target || params.source === params.target) return;
        const sourceStep = selectedWorkflow?.steps.find(step => step.id === params.source);
        const outcome: WorkflowEdgeOutcome = sourceStep?.stepType === 'decision'
            ? (params.sourceHandle === 'bottom' ? 'false' : 'true')
            : 'success';
        const newEdge = {
            ...params,
            id: v4(),
            animated: true,
            label: graphOutcomeLabel(outcome),
            data: {
                outcome,
                inputName: edges.some(edge => edge.target === params.target) ? params.source : 'input',
            },
            style: { stroke: graphOutcomeColor(outcome), strokeWidth: 2 },
        };
        setEdges(currentEdges => {
            const nextEdges = addEdge(newEdge, currentEdges.filter(edge => edge.id.indexOf('_decision_to_terminal') === -1));
            void persistGraphEdges(nextEdges);
            return nextEdges;
        });
    }, [edges, persistGraphEdges, selectedWorkflow?.steps, setEdges]);

    const handleDeleteEdges = useCallback((deletedEdges: any[]) => {
        const deletedIDs = new Set(deletedEdges.map(edge => edge.id));
        setEdges(currentEdges => {
            const nextEdges = currentEdges.filter(edge => !deletedIDs.has(edge.id));
            void persistGraphEdges(nextEdges);
            return nextEdges;
        });
    }, [persistGraphEdges, setEdges]);

    const handleDeleteNodes = (nodes: any[]) => {
        if (!user.credentials?.accessToken || selectedWorkflow?.id.startsWith("wf_template")) {
            dispatch(setNotification({
                type: 'error',
                message: 'Cannot delete steps from template workflows'
            }));
            return;
        }
        if (!user?.credentials?.accessToken || !selectedWorkflow?.id) {
            return;
        }
        for (let i = 0; i < nodes.length; i++) {
            deleteStep(user.credentials?.accessToken, selectedWorkflow?.id, nodes[i].id).then(async () => {
                getWorkflow(user.credentials!.accessToken, selectedWorkflow.id!).then((workflow) => {
                    setSelectedWorkflow(workflow);
                    dispatch(setNotification({
                        type: 'success',
                        message: 'Step deleted successfully'
                    }))
                }).catch(() => {
                    dispatch(setNotification({
                        type: 'error',
                        message: 'Failed to get workflow after deleting step'
                    }));
                });
            }).catch(() => {
                dispatch(setNotification({
                    type: 'error',
                    message: 'Failed to delete step'
                }))
            })
        }
    }

    useEffect(() => {
        const handleDeleteNodeEvent = (e: CustomEvent<{ id: string }>) => {
            const nodeId = e.detail.id;
            const nodeToDelete = nodes.find((n: any) => n.id === nodeId);
            if (nodeToDelete) {
                handleDeleteNodes([nodeToDelete]);
            }
        };
        window.addEventListener('delete-node', handleDeleteNodeEvent as EventListener);
        return () => {
            window.removeEventListener('delete-node', handleDeleteNodeEvent as EventListener);
        };
    }, [nodes, handleDeleteNodes]);

    useEffect(() => {
        if (!selectedWorkflow) {
            setSelectedWorkflow(workflows[0]);
        } else {
            const workflow = workflows.find(w => w.id === selectedWorkflow.id);
            if (!workflow) {
                setSelectedWorkflow(workflows[0]);
            } else {
                setSelectedWorkflow(workflow);
            }
        }
    }, [workflows])

    useEffect(() => {
        if (!selectedWorkflow) {
            return
        };
        const renderedEdges: any[] = [];
        const nodes: any[] = [];
        const steps = selectedWorkflow.steps;
        const graphEdges: WorkflowGraphEdge[] = selectedWorkflow.graph?.edges || steps.slice(0, -1).map((step, index) => ({
            id: `e${step.id}__${steps[index + 1].id}`,
            sourceNodeId: step.id!,
            targetNodeId: steps[index + 1].id!,
            outcome: step.stepType === 'decision' ? 'true' : 'success',
            inputName: 'input',
        }));
        const positions = layoutDAG(steps, graphEdges);

        // Process regular workflow steps
        for (let i = 0; i < steps.length; i++) {
            nodes.push({
                id: steps[i].id,
                type: steps[i].stepType,
                data: {
                    label: steps[i].stepType,
                    supportedTickers: supportedTickers,
                    assistants: assistants,
                    step: steps[i],
                    workflowID: selectedWorkflow.id,
                    userSecrets: userSecrets,
                    setShowTemplateCopyIconPulse,
                    setShowSignUpPopup,
                },
                position: {
                    x: positions[steps[i].id!]?.x || 0,
                    y: positions[steps[i].id!]?.y || 50
                },
                style: nodeStyle,
                sourcePosition: 'right',
                targetPosition: 'left',
            });
        }

        // Add terminal nodes for decision steps
        steps.forEach((step, i) => {
            if (step.stepType === 'decision') {
                const hasFalsePath = graphEdges.some(edge => edge.sourceNodeId === step.id && edge.outcome === 'false');
                if (hasFalsePath) return;
                const terminalId = `decision-${step.id}`;
                // Add terminal node below the decision node
                nodes.push({
                    id: terminalId,
                    type: 'terminal',
                    data: {},
                    position: {
                        // Position terminal node below its decision node
                        x: (nodes.find(n => n.id === step.id)?.position.x || 0) + 110,
                        y: (nodes.find(n => n.id === step.id)?.position.y || 0) + (step.decisionStep?.isSmartDecisionStep ? 250 : 350)
                    },
                });

                // Add edge from decision bottom handle to terminal node
                renderedEdges.push({
                    id: `e${step.id}_decision_to_terminal`,
                    source: step.id,
                    target: terminalId,
                    sourceHandle: "bottom", // Explicitly use the bottom handle
                    targetHandle: "top",    // Connect to the top handle of terminal node
                    animated: true,
                    style: {
                        stroke: "#c2400c",
                    },
                    label: "No"
                });
            }
        });

        // Add menu node
        nodes.push({
            id: 'menuNode',
            type: 'menuNode',
            position: {
                x: 0,
                y: -325,
            },
            data: {
                setSelectedWorkflow,
                workflow: selectedWorkflow,
                supportedTickers,
                setShowSignUpPopup,
                setShowTemplateCopyIconPulse,
            },
        });

        nodes.push({
            id: 'workflowExecutionLogNode',
            type: 'workflowExecutionLogNode',
            position: {
                x: 0,
                y: 300,
            },
            data: {
                workflow: selectedWorkflow,
            },
        })

        renderedEdges.push(...workflowGraphEdgesToFlow(graphEdges));

        setNodes(nodes);
        setEdges(renderedEdges);
    }, [selectedWorkflow, supportedTickers, assistants, userSecrets, setEdges, setNodes])

    const hanldeUpdateWorkflowEnabled = (workflow: Workflow) => {
        if (!user.credentials?.accessToken) {
            return;
        }
        updateWorkflow(user.credentials.accessToken, workflow.id, workflow.name || 'name missing', !workflow.enabled).then(() => {
            dispatch(setNotification({
                type: 'success',
                message: 'Workflow updated successfully'
            }));
            getWorkflow(user!.credentials!.accessToken, workflow.id).then((workflow) => {
                setSelectedWorkflow(workflow);
                setWorkflows(workflows.map((w) => {
                    if (w.id === workflow.id) {
                        return workflow;
                    }
                    return w;
                }));
            }).catch((reason) => {
                dispatch(setNotification({
                    type: 'error',
                    message: 'Failed to get workflow after updating'
                }));
            });
        }).catch((reason) => {
            dispatch(setNotification({
                type: 'error',
                message: `${reason}`
            }));
        });
    }

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const workflowIdFromUrl = queryParams.get('workflowId');
        const templateIdFromUrl = queryParams.get('templateId');

        if (workflowIdFromUrl || workflows.length > 0) {
            const targetId = workflowIdFromUrl || workflows[0].id;
            const workflowFromUrl = workflows.find(w => w.id === targetId);
            if (workflowFromUrl) {
                setSelectedWorkflow(workflowFromUrl);
            }
        }

        if (templateIdFromUrl || (workflows.length === 0 && workflowIdFromUrl === null)) {
            const targetId = templateIdFromUrl || workflowTemplates[0].id;
            const template = workflowTemplates.find(w => w.id === targetId);
            if (template) {
                setSelectedWorkflow(template);
                const queryParams = new URLSearchParams(window.location.search);
                queryParams.set('templateId', template.id);
                navigate(`/workflows?${queryParams.toString()}`, { replace: true });
            }
        }



    }, [workflows]);

    return (
        <>
            <NotificationPopup />
            <SignUpPopup isOpen={showSignUpPopup} closeModal={() => setShowSignUpPopup(false)} />
            {testFileWorkflow && user.credentials?.accessToken && (
                <WorkflowTestFilePicker
                    workflow={testFileWorkflow}
                    accessToken={user.credentials.accessToken}
                    onClose={() => setTestFileWorkflow(null)}
                    onRun={runWorkflowTest}
                />
            )}
            <div className="workflow-workspace grid grid-rows-[calc(100vh-60px)_60px] h-screen overflow-hidden">
                {/* Collapsed state toggle button - outside the grid for better positioning */}
                <button
                    className="fixed top-4 left-4 z-[999] bg-white p-2.5 rounded-md shadow-lg border-2 border-orange-500 md:hidden flex items-center gap-2"
                    style={{ display: isSidebarCollapsed ? 'flex' : 'none' }}
                    onClick={() => setIsSidebarCollapsed(false)}
                    aria-label="Open workflows list"
                >
                    <ChevronRightIcon className="h-5 w-5 text-orange-600" />
                    <span className="text-sm font-medium text-gray-700">Workflows</span>
                </button>

                <div className="grid grid-cols-1 h-full relative">
                    {/* Workflow List */}
                    <div
                        className={`absolute z-10 bg-white h-full transition-all duration-300 border-r border-gray-300
                            ${isSidebarCollapsed
                                ? 'w-0 -translate-x-full opacity-0'
                                : 'w-full md:w-[280px] lg:w-[300px] translate-x-0 opacity-100'}`}
                    >
                        <div className="p-2 sm:p-4 overflow-y-auto h-full">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold">Workflows</h2>
                                <div className="flex items-center gap-2">
                                    <PlusCircleIcon
                                        onClick={() => {
                                            if (!user.credentials?.accessToken) {
                                                setShowSignUpPopup(true);
                                                return;
                                            }
                                            createWorkflow(user.credentials!.accessToken!, 'New Workflow', [
                                                {
                                                    order: 1,
                                                    stepType: 'data-source',
                                                    dataSourceStep: {
                                                        subType: 'data-source-earnings',
                                                        ticker: supportedTickers[0]
                                                    }
                                                }
                                            ]).then((id: string) => {
                                                getAllWorkflows(user.credentials!.accessToken!).then((workflows) => {
                                                    setWorkflows(workflows);
                                                    setSelectedWorkflow(workflows.find(w => w.id === id));
                                                    const queryParams = new URLSearchParams(window.location.search);
                                                    queryParams.set('workflowId', id);
                                                    queryParams.delete('templateId');
                                                    navigate(`/workflows?${queryParams.toString()}`, { replace: true });
                                                }).catch((reason) => {
                                                    dispatch(setNotification({
                                                        type: 'error',
                                                        message: "" + reason
                                                    }));
                                                });
                                            }).catch((reason) => {
                                                dispatch(setNotification({
                                                    type: 'error',
                                                    message: 'Failed to create workflow'
                                                }));
                                            });
                                        }}
                                        className="text-gray-500 hover:text-black w-6 h-6 cursor-pointer"
                                    />
                                    <ChevronLeftIcon
                                        className="h-6 w-6 text-gray-500 hover:text-gray-700 cursor-pointer md:hidden"
                                        onClick={() => setIsSidebarCollapsed(true)}
                                        aria-label="Close workflows list"
                                    />
                                </div>
                            </div>

                            <ul className="space-y-2">
                                {workflows.map((w) => (
                                    <li
                                        key={w.id}
                                        className={`p-2 rounded-lg shadow hover:bg-gray-200 cursor-pointer flex items-center ${selectedWorkflow?.id === w.id ? 'bg-gray-200' : ''}`}
                                        onClick={() => {
                                            setSelectedWorkflow(w);
                                            const queryParams = new URLSearchParams(window.location.search);
                                            queryParams.set('workflowId', w.id);
                                            // Remove conversationId if present
                                            queryParams.delete('conversationId');
                                            queryParams.delete('templateId');
                                            navigate(`/workflows?${queryParams.toString()}`, { replace: true });
                                        }}
                                    >
                                        {showEditWorkflowName !== w.id &&
                                            <div className="flex items-center justify-between w-full">
                                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                                    {w.enabled ?
                                                        <ToggleRightIcon className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6" onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            hanldeUpdateWorkflowEnabled(w);
                                                        }} /> :
                                                        <ToggleLeftIcon className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6" onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            hanldeUpdateWorkflowEnabled(w);
                                                        }} />
                                                    }
                                                    <span className="flex-1 truncate font-medium" title={w.name}>
                                                        {w.name}
                                                    </span>
                                                </div>

                                                <div className="flex items-center gap-1 sm:gap-2">
                                                    <PlayIcon
                                                        className="w-5 h-5 text-green-700 cursor-pointer"
                                                        title="Test workflow"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (!user.credentials?.accessToken) {
                                                                return
                                                            }
                                                            setTestFileWorkflow(w);
                                                        }}
                                                    />
                                                    <PencilIcon
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setShowEditWorkflowName(w.id);
                                                        }}
                                                        className="w-5 h-5 text-gray-500 hover:text-blue-600 cursor-pointer"
                                                        title="Edit name"
                                                    />
                                                    <TrashIcon
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            deleteWorkflow(user.credentials!.accessToken, w.id).then(() => {
                                                                dispatch(setNotification({
                                                                    type: 'success',
                                                                    message: 'Workflow deleted successfully'
                                                                }));
                                                                getAllWorkflows(user.credentials!.accessToken!).then((workflows) => {
                                                                    setWorkflows(workflows);
                                                                    if (workflows.length === 0) {
                                                                        return;
                                                                    }
                                                                    setSelectedWorkflow(workflows[0]);
                                                                }).catch((reason) => {
                                                                    dispatch(setNotification({
                                                                        type: 'error',
                                                                        message: reason
                                                                    }));
                                                                });
                                                            }).catch((reason) => {
                                                                dispatch(setNotification({
                                                                    type: 'error',
                                                                    message: reason
                                                                }));
                                                            });
                                                        }}
                                                        className="w-5 h-5 text-gray-500 hover:text-red-600 cursor-pointer"
                                                        title="Delete workflow"
                                                    />
                                                </div>
                                            </div>
                                        }

                                        {showEditWorkflowName === w.id &&
                                            <div className="flex items-center w-full gap-1">
                                                <div className="flex-1">
                                                    <input
                                                        className="w-full rounded-md border-gray-300 p-1.5 text-gray-900 shadow-sm focus:ring-2 focus:ring-orange-600"
                                                        type="text"
                                                        value={w.name}
                                                        onChange={(e) => {
                                                            setWorkflows(workflows.map((workflow) => {
                                                                if (workflow.id === w.id) {
                                                                    return { ...workflow, name: e.target.value };
                                                                }
                                                                return workflow;
                                                            }));
                                                        }}
                                                        autoFocus
                                                    />
                                                </div>
                                                <div className="flex items-center shrink-0">
                                                    <button
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setShowEditWorkflowName('');
                                                            updateWorkflow(user.credentials!.accessToken!, w.id, w.name!, w.enabled).then(() => {
                                                                dispatch(setNotification({
                                                                    type: 'success',
                                                                    message: 'Workflow name updated successfully'
                                                                }));
                                                                getWorkflow(user.credentials!.accessToken!, w.id).then((workflow) => {
                                                                    setSelectedWorkflow(workflow);
                                                                    setWorkflows(workflows.map((wf) => {
                                                                        if (wf.id === w.id) {
                                                                            return workflow;
                                                                        }
                                                                        return wf;
                                                                    }));
                                                                }).catch((reason) => {
                                                                    console.error('Failed to get workflow after updating name', reason);
                                                                });
                                                            }).catch((reason) => {
                                                                dispatch(setNotification({
                                                                    type: 'error',
                                                                    message: `Failed to update workflow: ${reason}`
                                                                }));
                                                            });
                                                        }}
                                                        className="text-blue-600 whitespace-nowrap px-2"
                                                    >
                                                        Save
                                                    </button>
                                                    <div
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setShowEditWorkflowName('');
                                                            getAllWorkflows(user.credentials!.accessToken!).then((workflows) => {
                                                                setWorkflows(workflows);
                                                                if (workflows.length === 0) {
                                                                    return;
                                                                }
                                                                setSelectedWorkflow(workflows[0]);
                                                            }
                                                            ).catch((reason) => {
                                                                dispatch(setNotification({
                                                                    type: 'error',
                                                                    message: reason
                                                                }));
                                                            });
                                                        }}
                                                        className="flex items-center justify-center h-8 w-8 hover:bg-gray-300 rounded cursor-pointer"
                                                        title="Cancel"
                                                    >
                                                        <X className="w-5 h-5 text-gray-600 hover:text-red-600" />
                                                    </div>
                                                </div>
                                            </div>
                                        }
                                    </li>
                                ))}
                            </ul>

                            {workflows.length === 0 && (
                                <div className="text-center text-gray-500 mt-4">
                                    No workflows available. Create a new one!
                                </div>
                            )}

                            <div className="flex items-center justify-between mt-4 mb-4">
                                <h2 className="text-lg font-semibold">Templates</h2>
                            </div>
                            <ul className="space-y-2">
                                {workflowTemplates.map((w) => (
                                    <li
                                        key={w.id}
                                        className={`p-2 rounded-lg shadow hover:bg-gray-200 cursor-pointer flex items-center ${selectedWorkflow?.id === w.id ? 'bg-gray-200' : ''}`}
                                        onClick={() => {
                                            setSelectedWorkflow(w);
                                            const queryParams = new URLSearchParams(window.location.search);
                                            queryParams.set('templateId', w.id);
                                            setShowTemplateCopyIconPulse(false);
                                            navigate(`/workflows?${queryParams.toString()}`, { replace: true });
                                        }}
                                    >
                                        <span className="flex-1 truncate font-medium" title={w.name}>
                                            {w.name}
                                        </span>

                                        <CopyIcon
                                            className={`w-4 h-4 ml-2 ${showTemplateCopyIconPulse && selectedWorkflow?.id === w.id ? 'animate-pulse text-orange-500 drop-shadow-[0_0_4px_rgba(249,115,22,0.8)] transition-shadow duration-200' : ''}`}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                void copyTemplateWorkflow(w);
                                            }}
                                        />
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Collapsed state toggle button - more prominent and visible */}
                        <button
                            className={`fixed top-4 left-4 z-[50] bg-white p-2 rounded-md shadow-md border border-gray-300 md:hidden
                                ${isSidebarCollapsed ? 'opacity-100 visible' : 'opacity-0 invisible'} 
                                transition-opacity duration-300 flex items-center gap-1`}
                            onClick={() => setIsSidebarCollapsed(false)}
                            aria-label="Open workflows list"
                        >
                            <ChevronRightIcon className="h-5 w-5 text-gray-700" />
                            <span className="text-sm font-medium text-gray-700">Open Workflows</span>
                        </button>
                    </div>

                    {/* Main content area - ReactFlow */}
                    <div className={`h-full w-full transition-all duration-300 ${isSidebarCollapsed
                        ? 'ml-0'
                        : 'ml-0 md:ml-[280px] lg:ml-[300px]'
                        } relative`}>
                        {selectedWorkflow?.graph && (
                            <div className="absolute right-4 top-4 z-10 rounded-full border border-orange-200 bg-white/95 px-3 py-1.5 text-xs font-semibold tracking-wide text-orange-800 shadow-sm backdrop-blur">
                                DAG · version {selectedWorkflow.graph.version}
                            </div>
                        )}
                        <ReactFlow
                            nodes={nodes}
                            edges={edges}
                            onNodesChange={onNodesChange}
                            onEdgesChange={onEdgesChange}
                            onEdgesDelete={handleDeleteEdges}
                            onConnect={onConnect}
                            fitView
                            nodeTypes={nodeTypes}
                            onNodesDelete={handleDeleteNodes}
                            onBeforeDelete={({ nodes, edges }: { nodes: any, edges: any }): boolean | any => {

                                if (!user.credentials?.accessToken) {
                                    setShowSignUpPopup(true);
                                    return false
                                };

                                return { nodes, edges };
                            }}
                        >
                            <Controls />
                            <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
                        </ReactFlow>
                    </div>
                </div>

                <ToolbarComponent
                    handleUpdateConversation={handleUpdateConversation}
                    convo={convo}
                    convos={convos}
                    handleConversationClick={handleConversationClick}
                    handleCreateConversation={handleCreateConversation}
                    handleDeleteConversation={handleDeleteConversation}
                    supportedTickers={supportedTickers}
                    handleDeleteShareLink={handleDeleteShareLink}
                    handleCreateShareLink={handleCreateShareLink}
                />
            </div >
        </>
    );
};

const testFileTypeLabel = (type: WorkflowTestFile['type']): string => ({
    transcript: 'Transcript',
    earnings: 'Earnings report',
    usa_executive_order: 'Executive order',
    fed_press_release: 'Fed release',
    '8k_earnings': '8-K earnings',
    '8k': '8-K filing',
})[type] || type;

function WorkflowTestFilePicker({
    workflow,
    accessToken,
    onClose,
    onRun,
}: {
    workflow: Workflow;
    accessToken: string;
    onClose: () => void;
    onRun: (workflow: Workflow, file: WorkflowTestFile) => Promise<void>;
}) {
    const pageSize = 20;
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [offset, setOffset] = useState(0);
    const [files, setFiles] = useState<WorkflowTestFile[]>([]);
    const [total, setTotal] = useState(0);
    const [selectedFile, setSelectedFile] = useState<WorkflowTestFile | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isRunning, setIsRunning] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const requestRevision = useRef(0);

    useEffect(() => {
        const timeout = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
        return () => window.clearTimeout(timeout);
    }, [search]);

    useEffect(() => {
        const revision = ++requestRevision.current;
        setIsLoading(true);
        setError(null);
        getWorkflowTestFiles(accessToken, workflow.id, debouncedSearch, offset, pageSize)
            .then((response) => {
                if (requestRevision.current !== revision) return;
                setFiles(response.files || []);
                setTotal(response.total || 0);
            })
            .catch((reason) => {
                if (requestRevision.current !== revision) return;
                setFiles([]);
                setTotal(0);
                setError(reason?.message || 'Failed to load the file catalogue');
            })
            .finally(() => {
                if (requestRevision.current === revision) setIsLoading(false);
            });
    }, [accessToken, debouncedSearch, offset, workflow.id]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && !isRunning) onClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isRunning, onClose]);

    const handleRun = async () => {
        if (!selectedFile || isRunning) return;
        setIsRunning(true);
        setError(null);
        try {
            await onRun(workflow, selectedFile);
            onClose();
        } catch (reason: any) {
            setError(reason?.message || 'The test run could not be started');
            setIsRunning(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/55 p-3 backdrop-blur-[2px] sm:p-6"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget && !isRunning) onClose();
            }}
        >
            <section
                role="dialog"
                aria-modal="true"
                aria-labelledby="test-file-picker-title"
                className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl border border-slate-300 bg-[#fffdf9] shadow-2xl"
            >
                <div className="h-1.5 shrink-0 bg-orange-600" />
                <header className="flex items-start justify-between gap-6 border-b border-slate-200 px-5 py-4 sm:px-7">
                    <div className="min-w-0">
                        <div className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-700">
                            <FileSearch className="h-4 w-4" /> Test specimen
                        </div>
                        <h2 id="test-file-picker-title" className="truncate text-xl font-semibold text-slate-950 sm:text-2xl">
                            Choose a catalogue file
                        </h2>
                        <p className="mt-1 text-sm text-slate-600">
                            Run <span className="font-semibold text-slate-800">{workflow.name || 'this workflow'}</span> with one compatible source file.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isRunning}
                        className="rounded-md p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 disabled:opacity-40"
                        aria-label="Close file picker"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </header>

                <div className="border-b border-slate-200 bg-white px-5 py-4 sm:px-7">
                    <label htmlFor="workflow-test-file-search" className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-600">
                        Search the catalogue
                    </label>
                    <div className="relative">
                        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <input
                            id="workflow-test-file-search"
                            type="search"
                            value={search}
                            onChange={(event) => {
                                setSearch(event.target.value);
                                setOffset(0);
                                setSelectedFile(null);
                            }}
                            placeholder="Ticker, filename, fiscal period, or S3 key"
                            className="w-full rounded-lg border-slate-300 bg-slate-50 py-2.5 pl-10 pr-4 text-sm text-slate-900 shadow-inner placeholder:text-slate-400 focus:border-orange-500 focus:ring-orange-500"
                            autoFocus
                        />
                    </div>
                </div>

                <div className="min-h-[260px] flex-1 overflow-y-auto px-5 py-4 sm:px-7">
                    {isLoading && (
                        <div className="flex h-52 flex-col items-center justify-center gap-3 text-slate-500">
                            <LoaderCircle className="h-6 w-6 animate-spin text-orange-600" />
                            <span className="text-sm">Searching compatible files…</span>
                        </div>
                    )}

                    {!isLoading && error && (
                        <div className="flex h-52 flex-col items-center justify-center rounded-lg border border-red-200 bg-red-50 px-6 text-center">
                            <AlertTriangleIcon className="mb-3 h-6 w-6 text-red-600" />
                            <p className="font-semibold text-red-900">Catalogue unavailable</p>
                            <p className="mt-1 max-w-lg text-sm text-red-700">{error}</p>
                        </div>
                    )}

                    {!isLoading && !error && files.length === 0 && (
                        <div className="flex h-52 flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 text-center">
                            <FileSearch className="mb-3 h-7 w-7 text-slate-400" />
                            <p className="font-semibold text-slate-800">No compatible files found</p>
                            <p className="mt-1 text-sm text-slate-500">
                                {debouncedSearch ? 'Try a broader ticker, filename, or filing period.' : 'This data source does not have any catalogue files yet.'}
                            </p>
                        </div>
                    )}

                    {!isLoading && !error && files.length > 0 && (
                        <div className="space-y-2">
                            {files.map((file) => {
                                const selected = selectedFile?.id === file.id;
                                const period = [file.fiscalPeriod, file.fiscalYear].filter(Boolean).join(' ');
                                const date = file.fiscalDateEnding || file.createdAt;
                                return (
                                    <button
                                        key={file.id}
                                        type="button"
                                        aria-pressed={selected}
                                        onClick={() => setSelectedFile(file)}
                                        className={`group grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-lg border px-3.5 py-3 text-left transition sm:gap-4 sm:px-4 ${selected
                                            ? 'border-orange-500 bg-orange-50 shadow-[inset_3px_0_0_#ea580c]'
                                            : 'border-slate-200 bg-white hover:border-slate-400 hover:bg-slate-50'
                                            }`}
                                    >
                                        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${selected ? 'bg-orange-600 text-white' : 'bg-slate-100 text-slate-500 group-hover:text-slate-800'}`}>
                                            {selected ? <CheckCircle2 className="h-5 w-5" /> : <FileIcon className="h-5 w-5" />}
                                        </span>
                                        <span className="min-w-0">
                                            <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                                {file.ticker && <span className="font-mono text-xs font-bold text-orange-800">{file.ticker}</span>}
                                                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{testFileTypeLabel(file.type)}</span>
                                                {period && <span className="text-xs text-slate-500">{period}</span>}
                                            </span>
                                            <span className="mt-1 block truncate text-sm font-semibold text-slate-900" title={file.fileName || file.s3Key}>
                                                {file.fileName || file.s3Key}
                                            </span>
                                            <span className="mt-0.5 block truncate font-mono text-[11px] text-slate-400" title={file.s3Key}>{file.s3Key}</span>
                                        </span>
                                        <span className="hidden min-w-[100px] text-right sm:block">
                                            <span className="flex items-center justify-end gap-1 text-xs font-medium text-slate-500">
                                                <CalendarDays className="h-3.5 w-3.5" />
                                                {date ? dayjs(date).format('MMM D, YYYY') : 'Undated'}
                                            </span>
                                            {file.companyName && <span className="mt-1 block max-w-[170px] truncate text-xs text-slate-400">{file.companyName}</span>}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="flex shrink-0 flex-col gap-3 border-t border-slate-200 bg-white px-5 py-4 sm:px-7">
                    <div className="flex items-center justify-between gap-3 text-xs text-slate-500">
                        <span>{total > 0 ? `Showing ${offset + 1}–${Math.min(offset + pageSize, total)} of ${total}` : '0 files'}</span>
                        <div className="flex gap-2">
                            <button type="button" disabled={offset === 0 || isLoading} onClick={() => setOffset(Math.max(0, offset - pageSize))} className="rounded border border-slate-300 px-3 py-1.5 font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40">Previous</button>
                            <button type="button" disabled={offset + pageSize >= total || isLoading} onClick={() => setOffset(offset + pageSize)} className="rounded border border-slate-300 px-3 py-1.5 font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40">Next</button>
                        </div>
                    </div>
                    <div className="flex flex-col gap-3 border-t border-slate-100 pt-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0 text-sm">
                            <span className="text-slate-500">Selected specimen: </span>
                            <span className="font-semibold text-slate-900">{selectedFile ? (selectedFile.fileName || selectedFile.s3Key) : 'None'}</span>
                        </div>
                        <div className="flex shrink-0 gap-2">
                            <button type="button" onClick={onClose} disabled={isRunning} className="rounded-md px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-40">Cancel</button>
                            <button
                                type="button"
                                onClick={handleRun}
                                disabled={!selectedFile || isRunning}
                                className="inline-flex items-center justify-center gap-2 rounded-md bg-orange-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                            >
                                {isRunning ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <PlayIcon className="h-4 w-4" />}
                                {isRunning ? 'Starting test…' : 'Run selected file'}
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

function EmailNode({ id, data }: NodeProps<Node<{
    step: Step,
    workflowID: string,
    setShowTemplateCopyIconPulse: (value: boolean) => void,
    setShowSignUpPopup: (value: boolean) => void
}>>) {
    const { updateNodeData } = useReactFlow();
    const user = useAppSelector((state) => state.user);
    const dispatch = useDispatch();
    const [showSave, setShowSave] = useState(false);

    return (
        <div>
            <div className="space-y-3 w-72">
                <div className="text-black flex items-center justify-center">
                    Send Email
                    {showSave && (
                        <FileIcon
                            onClick={() => {
                                if (data.workflowID.startsWith("wf_template") && user?.credentials) {
                                    dispatch(setNotification({
                                        type: 'info',
                                        message: 'This workflow is a template. Click copy to edit it.'
                                    }));
                                    data.setShowTemplateCopyIconPulse(true);
                                    return;
                                }

                                if (!user?.credentials) {
                                    data.setShowSignUpPopup(true);
                                    return;
                                }

                                updateStep(user.credentials!.accessToken, data.workflowID, data.step.id!, data.step)
                                    .then(() => {
                                        dispatch(setNotification({
                                            type: 'success',
                                            message: 'Step saved successfully'
                                        }));
                                        setShowSave(false);
                                    })
                                    .catch((reason) => {
                                        console.error('Failed to save step', reason);
                                        dispatch(setNotification({
                                            type: 'error',
                                            message: 'Failed to save step' + reason
                                        }));
                                    });
                            }}
                            className="w-4 h-4 cursor-pointer ml-2"
                        />
                    )}
                    <StepOutputButton output={data.step.lastOutput} title="Email step output" subtitle="The result returned after sending this email" workflowID={data.workflowID} stepID={data.step.id} />
                    <TrashIcon
                        onClick={() =>
                            window.dispatchEvent(
                                new CustomEvent('delete-node', { detail: { id } })
                            )
                        }
                        className="absolute text-red-500 w-4 h-4 top-1 right-1 md:hidden cursor-pointer"
                    />
                </div>
                <div className="flex items-center space-x-2">
                    <label className="w-16 text-right text-black">Subject</label>
                    <input
                        className="flex-1 rounded-md border-gray-300 p-1.5 text-gray-900 shadow-sm focus:ring-2 focus:ring-orange-600"
                        onChange={(evt) => {
                            setShowSave(true);
                            updateNodeData(id, { step: { ...data.step, emailStep: { ...data.step.emailStep, subject: evt.target.value } } });
                        }}
                        value={data.step.emailStep?.subject}
                    />
                </div>
                <div className="flex items-center space-x-2">
                    <label className="w-16 text-right text-black">To</label>
                    <input
                        className="flex-1 bg-gray-100 rounded-md border-gray-300 p-1.5 text-gray-900 shadow-sm"
                        disabled
                        value={user.user?.email}
                    />
                </div>
                {/* checkbox item with label that says "auto transform body using AI" */}
                <div className="flex items-center space-x-2">
                    <input
                        type="checkbox"
                        checked={data.step.emailStep?.autoTransformBody || false}
                        onChange={(evt) => {
                            setShowSave(true);
                            updateNodeData(id, { step: { ...data.step, emailStep: { ...data.step.emailStep, autoTransformBody: evt.target.checked } } });
                        }}
                        className="rounded border-gray-300 text-orange-600 focus:ring-orange-600"
                    />
                    <div className='text-black'>Auto transform body using AI</div>
                    {/* info circle icon */}
                    <div className="relative group z-50 flex items-center">
                        <AlertTriangleIcon className="absolute transform w-4 h-4 text-yellow-500 cursor-pointer" />
                        <div className="absolute bottom-full mb-2 hidden group-hover:block w-64 p-2 bg-gray-700 text-white text-sm rounded-lg shadow-lg">
                            *We recommend using a JSON transform step instead of auto transform because LLMs miss details and the max output length is to short for transcripts*. <br /> <br /> If checked an LLM will be used to generate the body of the email based on the last output of the previous step. It is instructed to discard JSON and copy everything else verbatim. If unchecked, the body will be the output of the last step. JSON Transform steps best practice for getting text out of step outputs.
                        </div>
                    </div>
                </div>
            </div>
            <Handle type="target" position={Position.Left} />
        </div>
    );
}

function DataSourceNode({ id, data }: NodeProps<Node<{
    step: Step
    workflowID: string
    supportedTickers: string[],
    setShowTemplateCopyIconPulse: (value: boolean) => void,
    setShowSignUpPopup: (value: boolean) => void
}>>) {
    const { updateNodeData } = useReactFlow();
    const [allowDrag, setAllowDrag] = useState(true);
    const [showSave, setShowSave] = useState(false);
    const user = useAppSelector((state) => state.user);
    const dispatch = useDispatch();

    return (
        <div className={`space-y-3 w-64 ${!allowDrag ? 'nodrag' : ''}`}>
            <div className="text-black flex items-center justify-center">
                Data Source
                {showSave && <FileIcon
                    onClick={() => {
                        if (data.workflowID.startsWith("wf_template") && user?.credentials) {
                            dispatch(setNotification({
                                type: 'info',
                                message: 'This workflow is a template. Click copy to edit it.'
                            }));
                            data.setShowTemplateCopyIconPulse(true);
                            return;
                        }

                        if (!user?.credentials) {
                            data.setShowSignUpPopup(true);
                            return;
                        }

                        updateStep(user.credentials!.accessToken, data.workflowID, data.step.id!, data.step)
                            .then(() => {
                                dispatch(setNotification({
                                    type: 'success',
                                    message: 'Step saved successfully'
                                }));
                                setShowSave(false);
                            })
                            .catch((reason) => {
                                console.error('Failed to save step', reason);
                                dispatch(setNotification({
                                    type: 'error',
                                    message: 'Failed to save step' + reason
                                }));
                            });
                    }}
                    className="w-4 h-4 cursor-pointer ml-2"
                />}
                <StepOutputButton output={data.step.lastOutput} title="Data source output" subtitle={data.step.dataSourceStep?.ticker ? `${data.step.dataSourceStep.ticker} · ${data.step.dataSourceStep.subType}` : data.step.dataSourceStep?.subType} workflowID={data.workflowID} stepID={data.step.id} />
            </div>

            <div className="flex items-center space-x-2">
                <label className="w-16 text-right text-black">Type</label>
                <select
                    className="flex-1 w-full overflow-clip text-ellipsis rounded-md border-gray-300 p-1.5 text-gray-900 shadow-sm focus:ring-2 focus:ring-orange-600"
                    onChange={(evt) => {
                        setAllowDrag(true);
                        updateNodeData(id, { step: { ...data.step, dataSourceStep: { ...data.step.dataSourceStep, subType: evt.target.value } } });
                        setShowSave(true);
                    }}
                    onPointerDown={() => setAllowDrag(false)}
                    defaultValue={data.step.dataSourceStep?.subType}
                >
                    <option value="data-source-transcript">Earnings Call</option>
                    <option value="data-source-earnings">Earnings Report (10-Q/K)</option>
                    <option value="data-source-usa-executive-order">USA Executive/Presidential Order</option>
                    <option value="data-source-fed-press-release">FED press release</option>
                    <option disabled value="data-source-congressional-trades">Congressional trade</option>
                    <option value="data-source-8k">8-K (all 8-Ks, unfiltered)</option>
                    <option value="data-source-8k-earnings">8-K earnings early release (Item 2.02)</option>
                    <option disabled value="data-source-8k-new-material-agreement">8-K 1.01 new material agreement</option>
                    <option disabled value="data-source-8k-termination-of-material-agreement">8-K 1.02 termination of material agreement</option>
                    <option disabled value="data-source-8k-bankruptcy">8-K 1.03 bankruptcy/receivership</option>
                    <option disabled value="data-source-8k-asset-acquired-disposed">8-K 2.01 asset acquired/disposed</option>
                    <option disabled value="data-source-8k-new-financial-obligation">8-K 2.03 new financial obligation</option>
                    <option disabled value="data-source-8k-material impairment">8-K 2.06 material impairement</option>
                    <option disabled value="data-source-8k-delisting">8-K 3.01 delisting</option>
                    <option disabled value="data-source-8k-auditor-change">8-K 4.01 change of auditor</option>
                    <option disabled value="data-source-8k-unreliable-statements">8-K 4.02 non-reliance on previous report</option>
                    <option disabled value="data-source-8k-bylaws-ethics">8-K 5.03 changes to incorporation, bylaws, fiscal year</option>
                    <option disabled value="data-source-8k-regulation-fd">8-K 7.01 regulation FD</option>
                    <option disabled value="data-source-8k-acquisiton">8-K bankruptcy/receivership</option>

                </select>
            </div>

            <div className="flex items-center space-x-2">
                <label className="w-16 text-right text-black">Ticker</label>
                <select
                    disabled={!(data.step.dataSourceStep?.subType === 'data-source-earnings' || data.step.dataSourceStep?.subType === 'data-source-transcript' || data.step.dataSourceStep?.subType === 'data-source-8k-earnings' || data.step.dataSourceStep?.subType === 'data-source-8k')}
                    className="flex-1 rounded-md border-gray-300 p-1.5 text-gray-900 shadow-sm focus:ring-2 focus:ring-orange-600 disabled:bg-gray-200"
                    onChange={(evt) => {
                        setAllowDrag(true);
                        updateNodeData(id, { step: { ...data.step, dataSourceStep: { ...data.step.dataSourceStep, ticker: evt.target.value } } });
                        setShowSave(true);
                    }}
                    onPointerDown={() => setAllowDrag(false)}
                    defaultValue={data.step.dataSourceStep?.ticker}
                >
                    {data.supportedTickers.map(t => (
                        <option key={t} value={t}>{t}</option>
                    ))}
                </select>
            </div>


            <Handle type="source" position={Position.Right} />
        </div>
    );
}

function LLMNode({ id, data }: NodeProps<Node<{
    workflowID: string,
    step: Step,
    assistants: Assistant[],
    setShowTemplateCopyIconPulse: (value: boolean) => void,
    setShowSignUpPopup: (value: boolean) => void
}>>) {
    const user = useAppSelector((state) => state.user);
    const dispatch = useDispatch();
    const { updateNodeData } = useReactFlow();
    const [allowDrag, setAllowDrag] = useState(true);
    const [showSave, setShowSave] = useState(false);
    const [showPromptPopup, setShowPromptPopup] = useState(false);
    const [showStrucutedOutputPopup, setShowStructuredOutputPopup] = useState(false);
    return (
        <div>
            {showPromptPopup && <PopupInput onClose={() => setShowPromptPopup(false)} text={data.step.llmStep?.prompt || ''} setText={(value) => {
                setShowSave(true);
                updateNodeData(id, { step: { ...data.step, llmStep: { ...data.step.llmStep, prompt: value } } })
            }} />}
            {/* adding indentation to the below text will make it indented (ugly) in the rendered text area */}
            {showStrucutedOutputPopup && <JSONSchemaInput onClose={() => setShowStructuredOutputPopup(false)} text={data.step.llmStep?.structuredOutput || ''} setText={(value) => {
                setShowSave(true);
                updateNodeData(id, { step: { ...data.step, llmStep: { ...data.step.llmStep, structuredOutput: value } } })
            }} />}

            <div className={`${!allowDrag ? 'nodrag' : ''}`}>
                <div className="flex items-center justify-center text-black mb-2">
                    Natural Language Processing
                    {showSave && <FileIcon onClick={() => {
                        if (data.workflowID.startsWith("wf_template") && user?.credentials) {
                            dispatch(setNotification({
                                type: 'info',
                                message: 'This workflow is a template. Click copy to edit it.'
                            }));
                            data.setShowTemplateCopyIconPulse(true);
                            return;
                        }

                        if (!user?.credentials) {
                            data.setShowSignUpPopup(true);
                            return;
                        }

                        updateStep(user.credentials!.accessToken, data.workflowID, data.step.id!, data.step).then(() => {
                            dispatch(setNotification({
                                type: 'success',
                                message: 'Step saved successfully'
                            }));
                            setShowSave(false);
                        }).catch((reason) => {
                            console.error('Failed to save step', reason);
                            dispatch(setNotification({
                                type: 'error',
                                message: 'Failed to save step' + reason
                            }));
                        })
                    }} className="w-4 h-4 cursor-pointer ml-2" />}

                    <StepOutputButton output={data.step.lastOutput} title="LLM step output" subtitle={data.step.llmStep?.model ? `Generated by ${data.step.llmStep.model}` : 'Model response'} workflowID={data.workflowID} stepID={data.step.id} />
                    {
                        data?.step?.lastError && (
                            <div className="relative group left-5 z-50 flex items-center">
                                <CloudAlertIcon onClick={() => {
                                    navigator.clipboard.writeText(data.step.lastError || '');
                                    dispatch(setNotification({
                                        type: 'success',
                                        message: 'Copied error to clipboard'
                                    }));
                                }} className="absolute transform w-4 h-4 ml-2 cursor-pointer text-red-500" />
                                <div className="absolute bottom-full mb-2 hidden group-hover:block w-64 p-2 bg-gray-700 text-white text-sm rounded-lg shadow-lg">
                                    {'Last Error: ' + data.step.lastError}
                                </div>
                            </div>
                        )
                    }
                </div>
                <div className="flex items-center mb-2">
                    <div className='mr-2 text-black'>Prompt</div>
                    <div className="relative w-full max-w-xs">
                        <input
                            type="text"
                            placeholder="Enter text"
                            onChange={(evt) => {
                                setShowSave(true);
                                updateNodeData(id, { step: { ...data.step, llmStep: { ...data.step.llmStep, prompt: evt.target.value } } })
                            }}
                            value={data.step.llmStep?.prompt}
                            className="block w-full pr-8 truncate rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-orange-600 sm:text-sm sm:leading-6"
                        />
                        <ArrowsPointingOutIcon onClick={() => {
                            setShowPromptPopup(true)
                        }} className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500 cursor-pointer" />
                    </div>
                </div>
                <div className="flex items-center mb-2">
                    <div className='mr-2 text-black'>Model</div>
                    <select
                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-orange-600 sm:text-sm sm:leading-6"
                        onChange={(evt) => {
                            const model = evt.target.value

                            setAllowDrag(true);
                            updateNodeData(id, { step: { ...data.step, llmStep: { ...data.step.llmStep, model: model, reasoningEffort: model.startsWith("o") ? 'low' : model === "gpt-5.6-terra" ? 'medium' : model.startsWith("gpt-5") && model !== "gpt-5.6-luna" ? 'high' : undefined } } });
                            setShowSave(true);
                        }}
                        defaultValue={data.step.llmStep?.model}
                        onPointerDown={(e) => {
                            setAllowDrag(false);
                        }}
                    >
                        <option value="gpt-5.6-luna">5.6 Luna</option>
                        <option value="gpt-5.6-terra">5.6 Terra</option>
                        <option value="gpt-5.6-sol">5.6 Sol</option>
                        <option value="gpt-5.4">5.4</option>
                        <option value="gpt-4.1">4.1</option>
                        <option value="gpt-4.1-mini">4.1-mini</option>
                        <option value="gpt-4.1-nano">4.1-nano</option>
                        <option value="gpt-4o">4o</option>
                        <option value="gpt-4o-mini">4o-mini</option>
                        <option value="o3-pro">o3-pro</option>
                        <option value="o3">o3</option>
                        <option value="o3-mini">o3-mini</option>
                        <option value="o4-mini">o4-mini</option>
                    </select>
                    {(data.step.llmStep?.model?.startsWith("o") || (data.step.llmStep?.model?.startsWith("gpt-5") && data.step.llmStep.model !== "gpt-5.6-luna")) && (
                        <select className="ml-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-orange-600 sm:text-sm sm:leading-6"
                            onChange={(evt) => {
                                setAllowDrag(false);
                                updateNodeData(id, { step: { ...data.step, llmStep: { ...data.step.llmStep, reasoningEffort: evt.target.value } } });
                                setShowSave(true);
                            }}
                            value={data.step.llmStep?.reasoningEffort}
                            onPointerDown={() => {
                                setAllowDrag(false);
                            }}
                        >
                            <option value="low">low</option>
                            <option value="medium">medium</option>
                            <option value="high">high</option>
                        </select>
                    )}
                </div>
                <div className="flex items-center mb-2">
                    <div className='mr-2 text-black'>
                        Structured Output
                        <div className="relative group z-50 flex items-center">
                            <InformationCircleIcon className="absolute right-0 transform -translate-y-1/2 w-4 h-4 ml-2 text-gray-500 cursor-pointer" />
                            <div className="absolute bottom-full mb-2 hidden group-hover:block w-64 p-2 bg-gray-700 text-white text-sm rounded-lg shadow-lg">
                                Structured outputs use a JSON schema to enable transforming unstructured data into structured data. The output can be used in decision and other step types. Refer to the OpenAI docs for more info.
                            </div>
                        </div>
                    </div>
                    <div className="relative w-full max-w-xs">
                        <input
                            type="text"
                            placeholder="Enter JSON schema"
                            onChange={(evt) => {
                                setShowSave(true);
                                updateNodeData(id, { step: { ...data.step, llmStep: { ...data.step.llmStep, structuredOutput: evt.target.value } } })
                            }}
                            value={data.step.llmStep?.structuredOutput}
                            className="block w-full truncate pr-8 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-orange-600 sm:text-sm sm:leading-6"
                        />
                        <ArrowsPointingOutIcon onClick={() => {
                            setShowStructuredOutputPopup(true)
                        }} className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500 cursor-pointer" />
                    </div>
                </div>

                <div className="flex items-center mb-2">
                    <input
                        type="checkbox"
                        checked={data.step.llmStep?.useSystemPrompt || false}
                        onChange={(evt) => {
                            setShowSave(true);
                            updateNodeData(id, {
                                step: {
                                    ...data.step,
                                    llmStep: {
                                        ...data.step.llmStep,
                                        useSystemPrompt: evt.target.checked
                                    }
                                }
                            })
                        }}
                        className="rounded border-gray-300 text-orange-600 focus:ring-orange-600"
                    />
                    <div className='ml-2 text-black'>Enable system prompt</div>
                </div>

                <Handle type="target" position={Position.Left} />
                <Handle type="source" position={Position.Right} />
            </div>
        </div>

    );
}

function MenuNode({ id, data }: NodeProps<Node<{
    supportedTickers: string[];
    workflow: Workflow;
    setSelectedWorkflow: (w: Workflow) => void
    setShowSignUpPopup: (show: boolean) => void
    setShowTemplateCopyIconPulse: (show: boolean) => void
}>>) {
    const dispatch = useDispatch();
    const user = useAppSelector((state) => state.user);

    const commonAddStepPreCheck = (): boolean => {
        if (!user.credentials?.accessToken) {
            dispatch(setNotification({
                type: 'info',
                message: 'Please log in to create a workflow'
            }));

            data.setShowSignUpPopup(true);
            return false;
        }

        if (data.workflow.id?.startsWith("wf_template")) {
            dispatch(setNotification({
                type: 'info',
                message: 'This workflow is a template. Click copy to edit it.'
            }));

            data.setShowTemplateCopyIconPulse(true);

            return false;
        }

        return true;
    }

    const addDataSourceStep = () => {
        if (!commonAddStepPreCheck()) return;

        const dsStep = data.workflow.steps.find(e => e.stepType === 'data-source');
        if (dsStep !== undefined) {
            dispatch(setNotification({
                type: 'info',
                message: 'Workflows can only have 1 data source step'
            }))
            return;
        }

        createStep(user.credentials!.accessToken, data.workflow.id!, {
            order: 1,
            stepType: 'data-source',
            dataSourceStep: {
                subType: 'data-source-earnings',
                ticker: data.supportedTickers[0]
            }
        }).then(() => {
            getWorkflow(user.credentials?.accessToken!, data.workflow.id!).then((workflow) => {
                data.setSelectedWorkflow(workflow);
            }).catch((reason) => {
                dispatch(setNotification({
                    type: 'error',
                    message: reason
                }));
            })
        }).catch((reason) => {
            dispatch(setNotification({
                type: 'error',
                message: `${reason}`
            }));
        })
    }

    const addLLMStep = () => {
        if (!commonAddStepPreCheck()) return;

        createStep(user.credentials!.accessToken, data.workflow.id!, {
            order: data.workflow.steps.length + 1,
            stepType: 'llm',
            llmStep: {
                model: 'gpt-5.6-terra',
                reasoningEffort: 'medium',
                prompt: '',
                useSystemPrompt: true
            }
        }).then(() => {
            getWorkflow(user.credentials?.accessToken!, data.workflow.id!).then((workflow) => {
                data.setSelectedWorkflow(workflow);
            }).catch((reason) => {
                dispatch(setNotification({
                    type: 'error',
                    message: 'Failed to get workflow after adding LLM step'
                }));
            })
        }).catch((reason) => {
            dispatch(setNotification({
                type: 'error',
                message: `${reason}`
            }));
        })
    }

    const addEmailStep = () => {
        if (!commonAddStepPreCheck()) return;

        const existingStep = data.workflow.steps.find(e => e.stepType === 'email');

        if (existingStep !== undefined) {
            dispatch(setNotification({
                type: 'info',
                message: 'Only 1 email step is allowed per workflow'
            }))
            return
        }

        createStep(user.credentials!.accessToken, data.workflow.id!, {
            order: data.workflow.steps.length + 1,
            stepType: 'email',
            emailStep: {
                subject: `Workflow Execution: ${data.workflow.name}`,
                autoTransformBody: false,
            }
        }).then(() => {
            getWorkflow(user.credentials?.accessToken!, data.workflow.id!).then((workflow) => {
                data.setSelectedWorkflow(workflow);
            }).catch((reason) => {
                dispatch(setNotification({
                    type: 'error',
                    message: 'error getting workflow after adding email step'
                }));
            })
        }).catch((reason) => {
            dispatch(setNotification({
                type: 'error',
                message: "" + reason
            }))
        })
    }

    const addDecisionStep = () => {
        if (!commonAddStepPreCheck()) return;

        createStep(user.credentials!.accessToken, data.workflow.id!, {
            order: data.workflow.steps.length + 1,
            stepType: 'decision',
            decisionStep: {
                isSmartDecisionStep: true,
                smartDecisionStepQuestion: '',
                model: 'gpt-5.6-sol',
                targetAttribute: '',
                targetValue: '',
                operator: 'equal',
                attributeType: 'string'
            }
        }).then((stepId) => {
            getWorkflow(user.credentials?.accessToken!, data.workflow.id!).then((workflow) => {
                data.setSelectedWorkflow(workflow);
            }).catch((reason) => {
                dispatch(setNotification({
                    type: 'error',
                    message: 'Failed to get workflow after adding decision step'
                }));
            });
        }).catch((reason) => {
            dispatch(setNotification({
                type: 'error',
                message: `${reason}`
            }));
        });
    }

    const addApiDestinationStep = () => {
        if (!commonAddStepPreCheck()) return;

        createStep(user.credentials!.accessToken, data.workflow.id!, {
            order: data.workflow.steps.length + 1,
            stepType: 'api-destination',
            apiDestinationStep: {
                url: '',
                headers: {},
                body: '',
                method: 'POST',
                authHeaderName: '',
                authHeaderSecretName: '',
            }
        }).then((stepId) => {
            getWorkflow(user.credentials?.accessToken!, data.workflow.id!).then((workflow) => {
                data.setSelectedWorkflow(workflow);
            }).catch((reason) => {
                dispatch(setNotification({
                    type: 'error',
                    message: 'Failed to get workflow after adding decision step'
                }));
            });
        }).catch((reason) => {
            dispatch(setNotification({
                type: 'error',
                message: `${reason}`
            }));
        });
    }

    const addJsonTransformationStep = () => {
        if (!commonAddStepPreCheck()) return;

        createStep(user.credentials!.accessToken, data.workflow.id!, {
            order: data.workflow.steps.length + 1,
            stepType: 'json-transformation',
            jsonTransformationStep: {
                jmespath: ""
            }
        }).then((stepId) => {
            getWorkflow(user.credentials?.accessToken!, data.workflow.id!).then((workflow) => {
                data.setSelectedWorkflow(workflow);
            }).catch((reason) => {
                dispatch(setNotification({
                    type: 'error',
                    message: 'Failed to get workflow after adding json transformation step'
                }));
            });
        }).catch((reason) => {
            dispatch(setNotification({
                type: 'error',
                message: `${reason}`
            }));
        });
    }

    const addRedditPostStep = () => {
        if (!commonAddStepPreCheck()) return;

        createStep(user.credentials!.accessToken, data.workflow.id!, {
            order: data.workflow.steps.length + 1,
            stepType: 'reddit-action',
            redditStep: {
                subreddit: '',
                title: '',
                content: '',
                action: 'post',
            }
        }).then(() => {
            getWorkflow(user.credentials?.accessToken!, data.workflow.id!).then((workflow) => {
                data.setSelectedWorkflow(workflow);
            }).catch((reason) => {
                dispatch(setNotification({
                    type: 'error',
                    message: 'Failed to get workflow after adding Reddit Post step'
                }));
            })
        }).catch((reason) => {
            dispatch(setNotification({
                type: 'error',
                message: `${reason}`
            }));
        });
    };

    const addScheduleStep = () => {
        if (!commonAddStepPreCheck()) return;

        createStep(user.credentials!.accessToken, data.workflow.id!, {
            order: data.workflow.steps.length + 1,
            stepType: 'schedule',
            scheduleStep: {
                schedule: '0 * * * *'
            }
        }).then(() => {
            getWorkflow(user.credentials?.accessToken!, data.workflow.id!).then((workflow) => {
                data.setSelectedWorkflow(workflow);
            }).catch((reason) => {
                dispatch(setNotification({
                    type: 'error',
                    message: 'Failed to get workflow after adding schedule step'
                }));
            })
        }).catch((reason) => {
            dispatch(setNotification({
                type: 'error',
                message: `${reason}`
            }));
        });
    }

    return (
        <div className="bg-white shadow-lg rounded-lg p-2 w-50 border border-gray-200">
            <button onClick={addDataSourceStep} className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md">
                <DatabaseIcon className="w-4 h-4 mr-2" /> + Data Source
                <div className="relative group ml-auto">
                    <InformationCircleIcon className="w-4 h-4 ml-2 text-gray-500 cursor-pointer" />
                    <div className="absolute bottom-full mb-2 hidden group-hover:block w-64 p-2 bg-gray-700 text-white text-sm rounded-lg shadow-lg">
                        Data source events are triggered when a new document of that type is released.
                    </div>
                </div>
            </button>
            <button onClick={addLLMStep} className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md">
                <Cpu className="w-4 h-4 mr-2" /> + LLM Step
                <div className="relative group ml-auto">
                    <InformationCircleIcon className="w-4 h-4 ml-2 text-gray-500 cursor-pointer" />
                    <div className="absolute bottom-full mb-2 hidden group-hover:block w-96 p-2 bg-gray-700 text-white text-sm rounded-lg shadow-lg">
                        <p>Process and produce structured or unstructured data.</p>
                        <p className="">All models have access to tools for:</p>
                        <ul className="list-disc list-outside pl-5">
                            <li>Fetching fundamental data, risk/legal disclosures, etc.</li>
                            <li>Computing financial ratios</li>
                            <li>Semantic search of transcripts</li>
                            <li>Web search</li>
                        </ul>
                        <p className="flex"><InformationCircleIcon className="w-6 h-6 mr-1" />LLM steps have a system prompt enabled by default. Disabling it may hinder tool use</p>
                        <p className="flex"><InformationCircleIcon className="w-6 h-6 mr-1" />By default LLM step outputs are in the format of our API model for conversation messages.</p>
                        <p className="flex items-center"><AlertTriangleIcon className="w-4 h-4 mr-1" />Reasoning models can't execute code</p>
                    </div>
                </div>
            </button>
            <button onClick={addEmailStep} className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md">
                <Mail className="w-4 h-4 mr-2" /> + Email Step
                <div className="relative group ml-auto">
                    <InformationCircleIcon className="w-4 h-4 ml-2 text-gray-500 cursor-pointer" />
                    <div className="absolute bottom-full mb-2 hidden group-hover:block w-64 p-2 bg-gray-700 text-white text-sm rounded-lg shadow-lg">
                        Email steps use an LLM to convert the output of the previous step to email compatible format.
                    </div>
                </div>
            </button>
            <button onClick={addScheduleStep} className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md">
                <Clock className="w-4 h-4 mr-2 " /> + Schedule Step
                <div className="relative group ml-auto">
                    <InformationCircleIcon className="w-4 h-4 ml-2 text-gray-500 cursor-pointer" />
                    <div className="absolute bottom-full mb-2 hidden group-hover:block w-64 p-2 bg-gray-700 text-white text-sm rounded-lg shadow-lg">
                        Schedule steps happen in regular intervals or at specific times.
                    </div>
                </div>
            </button>

            <button onClick={() => {
                addDecisionStep()
            }} className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md">
                <Diamond className="w-4 h-4 mr-2 " /> + Decision Step
                <div className="relative group ml-auto">
                    <InformationCircleIcon className="w-4 h-4 ml-2 text-gray-500 cursor-pointer" />
                    <div className="absolute bottom-full mb-2 hidden group-hover:block w-64 p-2 bg-gray-700 text-white text-sm rounded-lg shadow-lg">
                        Decision steps act as a logic gate. Allowing execution to continue if the condition is met and stopping it if not.
                    </div>
                </div>
            </button>

            <button onClick={() => {
                addApiDestinationStep()
            }} className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md">
                <PlugIcon className="w-4 h-4 mr-2 " /> + API Destination Step
                <div className="relative group ml-auto">
                    <InformationCircleIcon className="w-4 h-4 ml-2 text-gray-500 cursor-pointer" />
                    <div className="absolute bottom-full mb-2 hidden group-hover:block w-64 p-2 bg-gray-700 text-white text-sm rounded-lg shadow-lg">
                        API destination steps send the output of the previous step to an external API.
                    </div>
                </div>
            </button>

            <button onClick={() => {
                addJsonTransformationStep()
            }} className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md">
                <CurlyBraces className="w-4 h-4 mr-2 " /> + JSON Transform
                <div className="relative group ml-auto">
                    <InformationCircleIcon className="w-4 h-4 ml-2 text-gray-500 cursor-pointer" />
                    <div className="absolute bottom-full mb-2 hidden group-hover:block w-64 p-2 bg-gray-700 text-white text-sm rounded-lg shadow-lg">
                        Use JMES path to transform the output of the previous step into a different format. Read more on <a href="https://jmespath.org/examples.html" target="_blank" rel="noreferrer" className="text-blue-500 hover:underline">jmespath.org</a>
                    </div>
                </div>
            </button>

            <button onClick={addRedditPostStep} className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md">
                <img src="/icons/reddit_icon.png" className="w-5 h-5 mr-2" /> + Reddit Step
                <div className="relative group ml-auto">
                    <InformationCircleIcon className="w-4 h-4 ml-2 text-gray-500 cursor-pointer" />
                    <div className="absolute bottom-full mb-2 hidden group-hover:block w-64 p-2 bg-gray-700 text-white text-sm rounded-lg shadow-lg">
                        Post to Reddit using your workflow output.
                    </div>
                </div>
            </button>
        </div>
    );
}

function DecisionStepNode({ id, data }: NodeProps<Node<{
    step: Step,
    workflowID: string,
    setShowTemplateCopyIconPulse: (value: boolean) => void,
    setShowSignUpPopup: (value: boolean) => void
}>>) {
    const { updateNodeData } = useReactFlow();
    const [allowDrag, setAllowDrag] = useState(true);
    const [showSave, setShowSave] = useState(false);
    const user = useAppSelector((state) => state.user);
    const dispatch = useDispatch();
    const [showSmartDecisionPopup, setShowSmartDecisionPopup] = useState(false);
    const [useCompoundLogic, setUseCompoundLogic] = useState(Boolean(data.step.decisionStep?.condition));
    const [conditionText, setConditionText] = useState(() => JSON.stringify(data.step.decisionStep?.condition || {}, null, 2));
    const [conditionError, setConditionError] = useState<string | null>(null);

    const createInitialCondition = (): DecisionCondition => ({
        all: [{
            path: data.step.decisionStep?.targetAttribute || 'status',
            operator: data.step.decisionStep?.operator || 'equal',
            value: data.step.decisionStep?.targetValue ?? 'ready',
            attributeType: data.step.decisionStep?.attributeType || 'string',
        }],
    });

    function parseValue(value: any) {
        if (typeof value === "number") {
            console.log("returning because already a number", value)
            return value
        }; // Already a number, return as-is
        if (typeof value === "string" && value.trim() !== "" && !isNaN(value as any)) {
            console.log("converting to number", value)
            return Number(value);
        }
        return value; // Preserve original value if not a valid number
    }

    return (
        <>
            {showSmartDecisionPopup && (
                <PopupInput
                    onClose={() => {
                        setShowSmartDecisionPopup((prev) => !prev);
                    }}
                    setText={(value) => {
                        setShowSave(true);
                        updateNodeData(id, {
                            step: {
                                ...data.step,
                                decisionStep: {
                                    ...data.step.decisionStep,
                                    smartDecisionStepQuestion: value
                                }
                            }
                        });
                    }}
                    text={data.step.decisionStep?.smartDecisionStepQuestion || ''}
                    placeholder="Enter question for the model"
                />
            )}
            <div className={`space-y-3 ${useCompoundLogic && !data.step.decisionStep?.isSmartDecisionStep ? 'w-[32rem]' : 'w-72'} ${!allowDrag ? 'nodrag' : ''}`}>
                <div className="text-black flex items-center justify-center">
                    Decision Step
                    {showSave && <FileIcon
                        onClick={() => {
                            if (conditionError) {
                                dispatch(setNotification({ type: 'error', message: conditionError }));
                                return;
                            }
                            if (data.workflowID.startsWith("wf_template") && user?.credentials) {
                                dispatch(setNotification({
                                    type: 'info',
                                    message: 'This workflow is a template. Click copy to edit it.'
                                }));
                                data.setShowTemplateCopyIconPulse(true);
                                return;
                            }

                            if (!user?.credentials) {
                                data.setShowSignUpPopup(true);
                                return;
                            }

                            updateStep(user.credentials!.accessToken, data.workflowID, data.step.id!, data.step)
                                .then(() => {
                                    dispatch(setNotification({
                                        type: 'success',
                                        message: 'Step saved successfully'
                                    }));
                                    setShowSave(false);
                                })
                                .catch((reason) => {
                                    console.error('Failed to save step', reason);
                                    dispatch(setNotification({
                                        type: 'error',
                                        message: 'Failed to save step: ' + reason
                                    }));
                                });
                        }}
                        className="w-4 h-4 cursor-pointer ml-2"
                    />}
                    <StepOutputButton output={data.step.lastOutput} title="Decision output" subtitle="The branch selected by this decision" workflowID={data.workflowID} stepID={data.step.id} />

                    {data?.step?.lastError && (
                        <div className="relative group left-5 z-50 flex items-center">
                            <CloudAlertIcon onClick={() => {
                                navigator.clipboard.writeText(data.step.lastError || '');
                                dispatch(setNotification({
                                    type: 'success',
                                    message: 'Copied error to clipboard'
                                }));
                            }} className="absolute transform w-4 h-4 ml-2 cursor-pointer text-red-500" />
                            <div className="absolute bottom-full mb-2 hidden group-hover:block w-64 p-2 bg-gray-700 text-white text-sm rounded-lg shadow-lg">
                                {'Last Error: ' + data.step.lastError}
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex items-center space-x-2">
                    <label className="w-28 text-right text-black">Smart Decision</label>
                    <input
                        type="checkbox"
                        checked={data.step.decisionStep?.isSmartDecisionStep}
                        onChange={(evt) => {
                            setShowSave(true);
                            updateNodeData(id, {
                                step: {
                                    ...data.step,
                                    decisionStep: {
                                        ...data.step.decisionStep,
                                        isSmartDecisionStep: evt.target.checked
                                    }
                                }
                            });
                        }}
                        className="rounded border-gray-300 text-orange-600 focus:ring-orange-600"
                    />
                </div>

                {data.step.decisionStep?.isSmartDecisionStep && (
                    <>
                        <div className="flex items-center space-x-2">
                            <label className="w-28 text-right text-black">Model</label>
                            <select
                                className="flex-1 rounded-md border-gray-300 p-1.5 text-gray-900 shadow-sm focus:ring-2 focus:ring-orange-600"
                                value={data.step.decisionStep?.model || 'gpt-4o'}
                                onChange={(evt) => {
                                    setAllowDrag(true);
                                    setShowSave(true);
                                    updateNodeData(id, {
                                        step: {
                                            ...data.step,
                                            decisionStep: {
                                                ...data.step.decisionStep,
                                                model: evt.target.value
                                            }
                                        }
                                    });
                                }}
                                onPointerDown={() => setAllowDrag(false)}
                            >
                                <option value="gpt-5.6-luna">5.6 Luna</option>
                                <option value="gpt-5.6-sol">5.6 Sol</option>
                                <option value="gpt-5.4">5.4</option>
                                <option value="gpt-4.1">4.1</option>
                                <option value="gpt-4.1-mini">4.1-mini</option>
                                <option value="gpt-4.1-nano">4.1-nano</option>
                                <option value="gpt-4o">4o</option>
                                <option value="gpt-4o-mini">4o-mini</option>
                                <option value="o3-pro">o3-pro</option>
                                <option value="o3">o3</option>
                                <option value="o3-mini">o3-mini</option>
                                <option value="o4-mini">o4-mini</option>
                            </select>
                        </div>

                        <div className="flex items-center space-x-2">
                            <label className="w-28 text-right text-black">Question</label>
                            <input
                                className="block w-full truncate pr-8 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-orange-600 sm:text-sm sm:leading-6"
                                value={data.step.decisionStep?.smartDecisionStepQuestion || ''}
                                onChange={(evt) => {
                                    setShowSave(true);
                                    updateNodeData(id, {
                                        step: {
                                            ...data.step,
                                            decisionStep: {
                                                ...data.step.decisionStep,
                                                smartDecisionStepQuestion: evt.target.value
                                            }
                                        }
                                    });
                                }}
                                placeholder="Enter question..."
                            />
                            <ArrowsPointingOutIcon onClick={() => {
                                setShowSmartDecisionPopup(true)
                            }} className="absolute right-3 transform w-5 h-5 text-gray-500 cursor-pointer" />
                        </div>
                    </>
                )}

                {!data.step.decisionStep?.isSmartDecisionStep && (
                    <>
                        <div className="flex items-center space-x-2 border-t border-slate-200 pt-3">
                            <label className="w-28 text-right text-black">Compound logic</label>
                            <input
                                type="checkbox"
                                checked={useCompoundLogic}
                                onChange={(evt) => {
                                    const enabled = evt.target.checked;
                                    setUseCompoundLogic(enabled);
                                    setShowSave(true);
                                    setConditionError(null);
                                    const condition = enabled ? (data.step.decisionStep?.condition || createInitialCondition()) : undefined;
                                    if (condition) setConditionText(JSON.stringify(condition, null, 2));
                                    updateNodeData(id, {
                                        step: {
                                            ...data.step,
                                            decisionStep: { ...data.step.decisionStep, condition }
                                        }
                                    });
                                }}
                                className="rounded border-gray-300 text-orange-600 focus:ring-orange-600"
                            />
                        </div>

                        {useCompoundLogic ? (
                            <div className="rounded-lg border border-slate-300 bg-slate-950 p-3 shadow-inner">
                                <div className="mb-2 flex items-center justify-between font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-300">
                                    <span>Logic expression</span>
                                    <span className="flex gap-2">
                                        <span className="text-emerald-400">ALL</span>
                                        <span className="text-sky-400">ANY</span>
                                        <span className="text-rose-400">NOT</span>
                                    </span>
                                </div>
                                <textarea
                                    aria-label="Compound decision logic"
                                    spellCheck={false}
                                    value={conditionText}
                                    onPointerDown={() => setAllowDrag(false)}
                                    onBlur={() => setAllowDrag(true)}
                                    onChange={(evt) => {
                                        const text = evt.target.value;
                                        setConditionText(text);
                                        setShowSave(true);
                                        try {
                                            const condition = JSON.parse(text) as DecisionCondition;
                                            const validationError = validateDecisionCondition(condition);
                                            setConditionError(validationError);
                                            if (!validationError) {
                                                updateNodeData(id, {
                                                    step: {
                                                        ...data.step,
                                                        decisionStep: { ...data.step.decisionStep, condition }
                                                    }
                                                });
                                            }
                                        } catch {
                                            setConditionError('Enter valid JSON before saving.');
                                        }
                                    }}
                                    className="nodrag h-48 w-full resize-y rounded border border-slate-700 bg-slate-900 p-2 font-mono text-xs leading-5 text-slate-100 outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                                />
                                <p className={`mt-2 text-xs ${conditionError ? 'text-rose-300' : 'text-slate-400'}`}>
                                    {conditionError || 'Use all/any arrays, not with one child, or path/operator/value/attributeType.'}
                                </p>
                            </div>
                        ) : (
                            <>
                        <div className="flex items-center space-x-2">
                            <label className="w-28 text-right text-black">Target Attribute</label>
                            <input



                                className="flex-1 rounded-md border-gray-300 p-1.5 text-gray-900 shadow-sm focus:ring-2 focus:ring-orange-600"
                                value={data.step.decisionStep?.targetAttribute || ''}
                                onChange={(evt) => {
                                    setShowSave(true);
                                    updateNodeData(id, {
                                        step: {
                                            ...data.step,
                                            decisionStep: {
                                                ...data.step.decisionStep,
                                                targetAttribute: evt.target.value
                                            }
                                        }
                                    });
                                }}
                                placeholder="jmespath compatible"
                            />
                        </div>

                        <div className="flex items-center space-x-2">
                            <label className="w-28 text-right text-black">Attribute Type</label>
                            <select
                                className="flex-1 rounded-md border-gray-300 p-1.5 text-gray-900 shadow-sm focus:ring-2 focus:ring-orange-600"
                                value={data.step.decisionStep?.attributeType || 'string'}
                                onChange={(evt) => {
                                    setAllowDrag(true);
                                    setShowSave(true);
                                    updateNodeData(id, {
                                        step: {
                                            ...data.step,
                                            decisionStep: {
                                                ...data.step.decisionStep,
                                                attributeType: evt.target.value
                                            }
                                        }
                                    });
                                }}
                                onPointerDown={() => setAllowDrag(false)}
                            >
                                <option value="string">String</option>
                                <option value="number">Number</option>
                            </select>
                        </div>

                        <div className="flex items-center space-x-2">
                            <label className="w-28 text-right text-black">Operator</label>
                            <select
                                className="flex-1 rounded-md border-gray-300 p-1.5 text-gray-900 shadow-sm focus:ring-2 focus:ring-orange-600"
                                value={data.step.decisionStep?.operator || 'equal'}
                                onChange={(evt) => {
                                    setAllowDrag(true);
                                    setShowSave(true);
                                    updateNodeData(id, {
                                        step: {
                                            ...data.step,
                                            decisionStep: {
                                                ...data.step.decisionStep,
                                                operator: evt.target.value
                                            }
                                        }
                                    });
                                }}
                                onPointerDown={() => setAllowDrag(false)}
                            >
                                <option value="equal">Equals</option>
                                <option value="not_equal">Not equals</option>
                                <option value="greater_than">Greater than</option>
                                <option value="less_than">Less than</option>
                                <option value="greater_than_or_equal">Greater than or equal</option>
                                <option value="less_than_or_equal">Less than or equal</option>
                                {data.step.decisionStep?.attributeType === 'string' && (
                                    <option value="contains">Contains</option>
                                )}
                            </select>
                        </div>

                        <div className="flex items-center space-x-2">
                            <label className="w-28 text-right text-black">Target Value</label>
                            <input
                                className="flex-1 rounded-md border-gray-300 p-1.5 text-gray-900 shadow-sm focus:ring-2 focus:ring-orange-600"
                                value={data.step.decisionStep?.targetValue}
                                onChange={(evt) => {
                                    setShowSave(true);
                                    updateNodeData(id, {
                                        step: {
                                            ...data.step,
                                            decisionStep: {
                                                ...data.step.decisionStep,
                                                targetValue: parseValue(evt.target.value)
                                            }
                                        }
                                    });
                                }}
                                placeholder="Value to compare against"
                            />
                        </div>
                            </>
                        )}
                    </>
                )}

                <Handle type="target" position={Position.Left} id="left" />
                {/* bottom handle should point to terminal node */}
                <Handle type="source" position={Position.Bottom} id="bottom" />
                <Handle type="source" position={Position.Right} id="right" />
            </div>
        </>
    );
}

interface PopupInputProps {
    text: string;
    setText: (value: string) => void;
    onClose: () => void;
    placeholder?: string;
}

function PopupInput({ text, setText, onClose, placeholder }: PopupInputProps) {
    return (
        <div className="fixed inset-0 flex items-center justify-center bg-opacity-50 z-[51]">
            <div className="bg-white p-6 rounded-lg shadow-lg relative">
                <button className="absolute top-2 right-2 text-gray-500 hover:text-gray-700" onClick={onClose}>
                    <X className="w-5 h-5" />
                </button>
                <textarea
                    className="resize p-2 border text-black rounded-md focus:ring-2 focus:ring-blue-400"
                    value={text}
                    placeholder={placeholder}
                    onChange={(e) => setText(e.target.value)}
                    rows={15}
                    cols={50}
                />
            </div>
        </div>
    );
}

function TerminalNode({ id }: NodeProps) {
    return (
        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-500 text-white font-bold shadow-lg">
            End
            <Handle type="target" position={Position.Top} id="top" />
        </div>
    );
}

function WorkflowExecutionLogNode({ id, data }: NodeProps<Node<{
    workflow: Workflow;
}>>) {
    const [activeTab, setActiveTab] = useState<'selected' | 'all'>('all');
    const [selectedExecution, setSelectedExecution] = useState<WorkflowExecutionLog | null>(null);
    const [showingLogDetails, setShowingLogDetails] = useState<{ executionID: string; stepLogID: string; input: string; output: string; title: string; subtitle: string } | null>(null);
    const dispatch = useDispatch();
    const user = useAppSelector((state) => state.user);

    // New state for pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [executionLogs, setExecutionLogs] = useState<WorkflowExecutionLog[]>([]);
    const [totalLogs, setTotalLogs] = useState(0);
    const [logsPerPage] = useState(5); // Or any other suitable page size
    const [isLoadingLogs, setIsLoadingLogs] = useState(false);
	const logRequestSequence = useRef(0);
	const pendingRunIDToFollow = useRef<string | undefined>(undefined);

    const fetchLogs = useCallback(async (page: number, runIDToFollow?: string) => {
		const requestSequence = ++logRequestSequence.current;
        if (!user.credentials?.accessToken || !data.workflow?.id || data.workflow.id.startsWith("wf_template")) {
            setExecutionLogs([]);
            setTotalLogs(0);
            setSelectedExecution(null); // Clear selected execution when workflow ID is missing
            return;
        }
        setIsLoadingLogs(true);
        try {
            const response = await getWorkflowExecutionLogsPaginated(
                user.credentials.accessToken,
                data.workflow.id,
                page,
                logsPerPage
            );
			if (requestSequence !== logRequestSequence.current) return;
            setExecutionLogs(response.logs);
            setTotalLogs(response.totalCount);
			if (page === 1 && response.logs[0]) {
				window.dispatchEvent(new CustomEvent('workflow-step-outputs-updated', {
					detail: {
						workflowID: data.workflow.id,
						stepLogs: response.logs[0].stepLogs,
					},
				}));
			}
			setShowingLogDetails(current => {
				if (!current) return current;
				const execution = response.logs.find(log => log.id === current.executionID);
				const stepLog = execution?.stepLogs.find(log => log.id === current.stepLogID);
				if (!stepLog) return current;
				return {
					...current,
					input: stepLog.input,
					output: stepLog.output,
					subtitle: `${stepLog.status.charAt(0).toUpperCase() + stepLog.status.slice(1)} · ${stepLog.duration || 'No duration recorded'}`,
				};
			});
            setSelectedExecution(current => {
				if (runIDToFollow) {
					return response.logs.find(log => log.id === runIDToFollow) ?? current;
				}
                if (!current) return current;
                return response.logs.find(log => log.id === current.id) ?? current;
            });
			if (runIDToFollow && response.logs.some(log => log.id === runIDToFollow)) {
				pendingRunIDToFollow.current = undefined;
				setActiveTab('selected');
			}
        } catch (error) {
			if (requestSequence !== logRequestSequence.current) return;
            console.error("Failed to fetch workflow execution logs:", error);
            dispatch(setNotification({ type: 'error', message: 'Failed to load execution logs.' }));
            setExecutionLogs([]);
            setTotalLogs(0);
            setSelectedExecution(null);
        } finally {
			if (requestSequence === logRequestSequence.current) setIsLoadingLogs(false);
        }
    }, [data.workflow?.id, user.credentials?.accessToken, logsPerPage, dispatch]);

    useEffect(() => {
		fetchLogs(currentPage, pendingRunIDToFollow.current);
    }, [fetchLogs, currentPage, data.workflow?.id]); // Added data.workflow?.id to refetch if workflow changes

    useEffect(() => {
		const refreshProgress = (event: Event) => {
			const metadata = (event as CustomEvent<Record<string, string>>).detail || {};
			if (metadata.workflow_id && metadata.workflow_id !== data.workflow.id) return;
			const startedRunID = metadata.run_status === 'running' && !metadata.node_id
				? metadata.workflow_run_id
				: undefined;
			if (startedRunID) {
				pendingRunIDToFollow.current = startedRunID;
				setCurrentPage(1);
				void fetchLogs(1, startedRunID);
				return;
			}
			void fetchLogs(currentPage, pendingRunIDToFollow.current);
		};
		window.addEventListener('workflow-execution-updated', refreshProgress);
		return () => window.removeEventListener('workflow-execution-updated', refreshProgress);
	}, [data.workflow.id, fetchLogs, currentPage]);

    const handleRetry = (workflowId: string, executionLogId: string) => {
        if (!user.credentials?.accessToken) {
            dispatch(setNotification({ type: 'error', message: 'Authentication required to retry.' }));
            return;
        }
        if (!workflowId || !executionLogId) {
            dispatch(setNotification({ type: 'error', message: 'Missing IDs for retry.' }));
            return;
        }

        retryWorkflowExecution(user.credentials.accessToken, workflowId, executionLogId)
            .then(() => {
                dispatch(setNotification({
                    type: 'info',
                    message: 'Workflow retry initiated successfully.'
                }));
                // Optionally, refresh logs or update UI state here
            })
            .catch((error: Error) => {
                dispatch(setNotification({
                    type: 'error',
                    message: error.message || 'Failed to initiate workflow retry.'
                }));
            });
    };

    const getStatusTextStyling = (status: string) => {
        if (status === "success") {
            return "text-green-600 bg-green-300/[.6]";
        } else if (status === "failed") {
            return "text-red-600 bg-red-300/[.6]";
        } else if (status === "skipped") {
            return "text-slate-600 bg-slate-200";
        } else {
            return "text-yellow-600 bg-yellow-200/[.6]";
        }
    }

    const getStepTitle = (step: Step | undefined) => {
        if (step === undefined) {
            return "Unknown";
        }
        switch (step.stepType) {
            case "data-source":
                switch (step.dataSourceStep?.subType) {
                    case "data-source-transcript":
                        return "Earnings call released";
                    case "data-source-earnings":
                        return "Earnings report released";
                    case "data-source-usa-executive-order":
                        return "Receive executive order";
                    case "data-source-8k-earnings":
                        return "Receive 8-K earnings";
                    default:
                        return "Receive data input";
                }
            case "llm":
                if (step.llmStep?.structuredOutput) {
                    return "Return structured data";
                }
                return "Ask AI a question";
            case "email":
                return "Send an email";
            case "decision":
                return "Logic Gate";
            case "api-destination":
                return "Send data to API";
            case "json-transformation":
                return "Transform data";
            case "reddit-action":
                return "Reddit Action";
            case "schedule":
                return "Scheduled Execution";
            default:
                return "Unknown";
        }
    }

    const getStatusIcon = (status: StepStatus) => {
        if (status === "success") {
            return <Check className="w-4 h-4 text-green-500" />;
        } else if (status === "failed") {
            return <X className="w-4 h-4 text-red-500" />;
        } else if (status === "skipped") {
            return <ArrowLeftRight className="w-4 h-4 text-slate-500" />;
        } else if (status === "executing") {
            return <Zap className="w-4 h-4 text-yellow-500" />;
        } else {
            return <Clock className="w-4 h-4 text-yellow-500" />;
        }
    }

    const getStepIcon = (stepType: StepType) => {
        const wh = 'w-6 h-6 '
        switch (stepType) {
            case "data-source":
                return <DatabaseIcon className={wh + "w-4 h-4 text-gray-500"} />;
            case "llm":
                return <Cpu className={wh + "text-gray-500"} />;
            case "email":
                return <Mail className={wh + "text-gray-500"} />;
            case "decision":
                return <Diamond className={wh + "text-gray-500"} />;
            case "json-transformation":
                return <CurlyBraces className={wh + "text-gray-500"} />;
            case "api-destination":
                return <PlugIcon className={wh + "text-gray-500"} />;
            case "reddit-action":
                return <img src="/icons/reddit_icon.png" className={wh + "text-gray-500"} />;
            case "schedule":
                return <Clock className={wh + "text-gray-500"} />;
            default:
                return <DatabaseIcon className={wh + "text-gray-500"} />;
        }
    }

    const getWorkflowStatus = (workflow: WorkflowExecutionLog): StepStatus => {
        if (workflow.stepLogs.length === 0) {
            return 'executing';
        }
        // if any step failed, the workflow is considered failed
        if (workflow.stepLogs.some(log => log.status === 'failed')) {
            return 'failed';
        }
        // if all steps are successful, the workflow is considered successful
        if (workflow.stepLogs.every(log => log.status === 'success' || log.status === 'skipped')) {
            return 'success';
        }
        // if any step is running, the workflow is considered running
        if (workflow.stepLogs.some(log => log.status === 'executing')) {
            return 'executing';
        }

        return 'idle';
    }

    const getWorkflowFooterText = (workflow: WorkflowExecutionLog): string => {
        if (workflow.stepLogs.length === 0) {
            return 'Workflow is executing';
        }
        // if any step failed, the workflow is considered failed
        if (workflow.stepLogs.some(log => log.status === 'failed')) {
            // Find the first failed step to report its order/index
            const failedStepIndex = workflow.stepLogs.findIndex(log => log.status === 'failed');
            const stepOrder = data.workflow.steps.find(s => s.id === workflow.stepLogs[failedStepIndex].stepId)?.order || failedStepIndex + 1;
            return `Run failed on step ${stepOrder}`;
        }
        // if all steps are successful, the workflow is considered successful
        if (workflow.stepLogs.every(log => log.status === 'success' || log.status === 'skipped')) {
            return 'Run completed successfully';
        }
        // if any step is running, the workflow is considered running
        if (workflow.stepLogs.some(log => log.status === 'executing')) {
            return 'Workflow is executing';
        }

        return 'Workflow is idle';
    }

    useEffect(() => {
        // If activeTab was 'selected' but there's no selectedExecution (e.g. after logs load and none are there),
        // switch to 'all' tab.
        if (activeTab === 'selected' && !selectedExecution && executionLogs.length === 0) {
            setActiveTab('all');
        }

    }, [executionLogs, selectedExecution, activeTab]);


    const totalPages = Math.ceil(totalLogs / logsPerPage);

    return (
        <>
            {showingLogDetails && (
                <StepDataInspector
                    title={showingLogDetails.title}
                    subtitle={showingLogDetails.subtitle}
                    panels={[
                        { id: 'output', label: 'Output', value: showingLogDetails.output },
                        { id: 'input', label: 'Input', value: showingLogDetails.input },
                    ]}
                    initialPanelId="output"
                    onClose={() => setShowingLogDetails(null)}
                />
            )}
            <div className="bg-white shadow-lg rounded-lg p-4 border border-gray-200 w-2xl flex flex-col flex-grow">
                <div className="flex justify-between items-center mb-4">
                    <div className="flex border-b border-gray-200">
                        <button
                            className={`px-4 py-2 text-sm -mb-px ${activeTab === 'all'
                                ? 'text-orange-600 border-b-2 border-orange-500 font-medium'
                                : 'text-gray-500 hover:text-gray-700'}`}
                            onClick={() => setActiveTab('all')}
                        >
                            All Executions
                        </button>
                        <button
                            className={`px-4 py-2 text-sm -mb-px ${activeTab === 'selected'
                                ? 'text-orange-600 border-b-2 border-orange-500 font-medium'
                                : 'text-gray-500 hover:text-gray-700'}`}
                            onClick={() => setActiveTab('selected')}
                        >
                            Selected Execution
                        </button>
                        <button
                            onClick={() => {
                                dispatch(setNotification({
                                    message: 'Refreshing Logs',
                                    type: 'info',
                                }))
                                fetchLogs(currentPage)
                            }}
                            className="flex items-center px-3 py-1.5 disabled:opacity-60"
                            disabled={isLoadingLogs}
                            title="Refresh Workflows"
                        >
                            <RefreshCwIcon className={`w-4 h-4 mr-1 ${isLoadingLogs ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>

                <div>
                    {activeTab === 'selected' && selectedExecution ? (
                        <>
                            <ul className="space-y-4">
                                {selectedExecution?.stepLogs?.map((log, index) => {
                                    return (
                                        <li
                                            key={`selected-${log.id}-${log.stepId}`}
                                            className="p-4 bg-white-50 rounded-lg border border-gray-300"
                                        >
                                            <div className="flex items-center space-x-2">
                                                {getStepIcon(log.stepType)}
                                                <span className="text-xl">{getStepTitle(data.workflow.steps.find(e => e.id === log.stepId) || {
                                                    stepType: log.stepType,
                                                    order: 0,
                                                })}</span>
                                            </div>
                                            <div
                                                className="border border-grey rounded p-2 mt-4 w-full flex items-center justify-between cursor-pointer text-gray-600 hover:text-gray-800"
                                                onClick={() => setShowingLogDetails({
													executionID: selectedExecution.id,
													stepLogID: log.id,
                                                    input: log.input,
                                                    output: log.output,
                                                    title: getStepTitle(data.workflow.steps.find(step => step.id === log.stepId) || { stepType: log.stepType, order: 0 }),
                                                    subtitle: `${log.status.charAt(0).toUpperCase() + log.status.slice(1)} · ${log.duration || 'No duration recorded'}`,
                                                })}
                                            >
                                                <div className="flex items-center space-x-2">
                                                    <ArrowLeftRight className="w-4 h-4" />
                                                    <span>See all inputs and outputs</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center">
                                                <div className="text-sm mt-2 max-w-fit text-gray-700 bg-gray-200/[.6] rounded p-1 flex items-center space-x-2">
                                                    <Clock className="w-4 h-4 text-gray-500" />
                                                    <span>{log.duration}</span>
                                                </div>
                                                <div className={`text-sm ml-2 mt-2 max-w-fit ${getStatusTextStyling(log.status)} text-green-600 rounded p-1 text-gray-700 flex items-center space-x-2`}>
                                                    {getStatusIcon(log.status)}
                                                    <span>{log.status.charAt(0).toUpperCase() + log.status.slice(1)}</span>
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                            <div className={`w-full ${getWorkflowStatus(selectedExecution!) === 'failed' ? 'bg-red-100' : getWorkflowStatus(selectedExecution!) === 'success' ? 'bg-green-100' : 'bg-yellow-100'} p-2 rounded-md mt-4`}>
                                <div className="text-left w-full flex items-center">
                                    {getStatusIcon(getWorkflowStatus(selectedExecution!))}
                                    <p className={`text-sm ml-2 ${getWorkflowStatus(selectedExecution!) === 'failed' ? 'text-red-600' : getWorkflowStatus(selectedExecution!) === 'success' ? 'text-green-600' : 'text-yellow-600'
                                        } font-bold`}>{getWorkflowFooterText(selectedExecution!)}</p>
                                </div>
                                {
                                    selectedExecution.duration && (
                                        <div className="text-left w-full flex items-center">
                                            <Clock className="w-4 h-4 text-gray-500 mr-2" />
                                            {selectedExecution?.duration}
                                        </div>
                                    )
                                }
                            </div>
                            {
                                selectedExecution!.isTestExecution && (
                                    <div className={`text-left font-bold w-full mt-2 p-2 flex items-center ${getStatusTextStyling("")}`}>
                                        <FlaskConical className="w-4 h-4 text-yellow-600 mr-2" />
                                        Test Execution
                                    </div>
                                )
                            }
                            <button
                                onClick={() => handleRetry(selectedExecution.workflowId, selectedExecution.id)}
                                className="mt-2 flex items-center justify-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                title="Retry this execution"
                            >
                                <RefreshCwIcon className="w-4 h-4 mr-2" />
                                Retry Execution
                            </button>
                        </>
                    ) : (
                        <ul className="divide-y divide-gray-200">
                            {isLoadingLogs && <p className="text-center py-3">Loading logs...</p>}
                            {!isLoadingLogs && executionLogs.length === 0 && (
                                <p className="text-center py-3 text-gray-500">No execution logs found for this workflow.</p>
                            )}
                            {!isLoadingLogs && executionLogs.map((log, index) => {
                                const executionDate = dayjs(log.startTime).format('MMM D, YYYY • h:mm A');
                                return (
                                    <li key={`all-${log.id}-${index}`} className="py-3 flex items-center justify-between hover:bg-gray-50 cursor-pointer px-2 rounded-md">
                                        <div className="flex items-center space-x-2 flex-grow" onClick={() => {
                                            setSelectedExecution(log);
                                            setActiveTab('selected');
                                        }}>
                                            <div className="flex items-cetner">
                                                <p className="text-sm">{executionDate}</p>
                                            </div>
                                        </div>

                                        <div className={`text-sm ${getStatusTextStyling(getWorkflowStatus(log))} ml-2 rounded p-1 text-gray-700 flex items-center space-x-2`}>
                                            {getStatusIcon(getWorkflowStatus(log))}
                                            <span>{getWorkflowStatus(log).charAt(0).toUpperCase() + getWorkflowStatus(log).slice(1)}</span>
                                        </div>
                                        <button
                                            onClick={() => handleRetry(log.workflowId, log.id)}
                                            className="ml-2 p-1 text-gray-500 hover:text-blue-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            title="Retry this execution"
                                        >
                                            <RefreshCwIcon className="w-4 h-4" />
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
                {/* Pagination Controls */}
                {totalLogs > 0 && activeTab === 'all' && (
                    <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={isLoadingLogs || currentPage === 1}
                            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>
                        <span className="text-sm text-gray-700">
                            Page {currentPage} of {totalPages}
                        </span>
                        <button
                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={isLoadingLogs || currentPage === totalPages || totalLogs === 0}
                            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};

function ApiDestinationNode({ id, data }: NodeProps<Node<{
    step: Step,
    userSecrets: Secret[],
    workflowID: string,
    setShowTemplateCopyIconPulse: (value: boolean) => void,
    setShowSignUpPopup: (value: boolean) => void
}>>) {
    const { updateNodeData } = useReactFlow();
    const [allowDrag, setAllowDrag] = useState(true);
    const [showSave, setShowSave] = useState(false);
    const user = useAppSelector((state) => state.user);
    const dispatch = useDispatch();
    const [showHeadersPopup, setShowHeadersPopup] = useState(false);

    // Parse headers - ensure we work with an object
    const getHeadersObject = () => {
        const headers = data.step.apiDestinationStep?.headers;
        if (!headers) return {};
        if (typeof headers === 'string') {
            try {
                return JSON.parse(headers);
            } catch (e) {
                return {};
            }
        }
        return headers;
    };

    const headersCount = Object.keys(getHeadersObject()).length;
    const { body } = data.step.apiDestinationStep || {};
    return (
        <>
            {showHeadersPopup && (
                <HeadersTablePopup
                    headers={getHeadersObject()}
                    onSave={(updatedHeaders) => {
                        setShowSave(true);
                        updateNodeData(id, {
                            step: {
                                ...data.step,
                                apiDestinationStep: {
                                    ...data.step.apiDestinationStep,
                                    headers: updatedHeaders
                                }
                            }
                        });
                        setShowHeadersPopup(false);
                    }}
                    onClose={() => setShowHeadersPopup(false)}
                />
            )}

            <div className={`bg-white p-4 w-full flex flex-col ${!allowDrag ? 'nodrag' : ''}`}>
                <div className="text-black flex items-center justify-center mb-2">
                    API Destination Step
                    {showSave && <FileIcon
                        onClick={() => {
                            if (data.workflowID.startsWith("wf_template") && user?.credentials) {
                                dispatch(setNotification({
                                    type: 'info',
                                    message: 'This workflow is a template. Click copy to edit it.'
                                }));
                                data.setShowTemplateCopyIconPulse(true);
                                return;
                            }

                            if (!user?.credentials) {
                                data.setShowSignUpPopup(true);
                                return;
                            }

                            updateStep(user.credentials!.accessToken, data.workflowID, data.step.id!, data.step)
                                .then(() => {
                                    dispatch(setNotification({
                                        type: 'success',
                                        message: 'Step saved successfully'
                                    }));
                                    setShowSave(false);
                                })
                                .catch((reason) => {
                                    console.error('Failed to save step', reason);
                                    dispatch(setNotification({
                                        type: 'error',
                                        message: 'Failed to save step: ' + reason
                                    }));
                                });
                        }}
                        className="w-4 h-4 cursor-pointer ml-2"
                    />}
                    <StepOutputButton output={data.step.lastOutput} title="API destination output" subtitle={`${data.step.apiDestinationStep?.method || 'Request'} ${data.step.apiDestinationStep?.url || ''}`.trim()} workflowID={data.workflowID} stepID={data.step.id} />
                </div>
                <div className="flex items-center mb-2">
                    <label className="w-28 text-right text-black">Endpoint</label>
                    <input
                        className="block ml-2 w-full truncate pr-8 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-orange-600 sm:text-sm sm:leading-6"
                        value={data.step.apiDestinationStep?.url || ''}
                        onChange={(evt) => {
                            setAllowDrag(true);
                            setShowSave(true);
                            updateNodeData(id, {
                                step: {
                                    ...data.step,
                                    apiDestinationStep: {
                                        ...data.step.apiDestinationStep,
                                        url: evt.target.value
                                    }
                                }
                            })
                        }}
                        onPointerDown={() => setAllowDrag(false)}
                        placeholder="Enter endpoint..."
                    />
                </div>
                <div className="flex items-center mb-2">
                    <label className="w-28 text-right text-black">Method</label>
                    <select
                        className="flex-1 ml-2 rounded-md border-gray-300 p-1.5 text-gray-900 shadow-sm focus:ring-2 focus:ring-orange-600"
                        value={data.step.apiDestinationStep?.method || 'POST'}
                        onChange={(evt) => {
                            setAllowDrag(true);
                            setShowSave(true);
                            updateNodeData(id, {
                                step: {
                                    ...data.step,
                                    apiDestinationStep: {
                                        ...data.step.apiDestinationStep,
                                        method: evt.target.value
                                    }
                                }
                            })
                        }}
                        onPointerDown={() => setAllowDrag(false)}
                    >
                        <option value="POST">POST</option>
                        <option value="PUT">PUT</option>
                    </select>
                </div>
                <div className="flex items-center mb-2">
                    <label className="w-28 text-right text-black">Headers</label>
                    <div className="relative w-full ml-2">
                        <div
                            className="flex items-center justify-between w-full pr-8 truncate rounded-md py-1.5 px-2 text-gray-900 ring-1 ring-inset ring-gray-300 cursor-pointer hover:bg-gray-50"
                            onClick={() => setShowHeadersPopup(true)}
                            onPointerDown={() => setAllowDrag(false)}
                        >
                            <span>{headersCount} header{headersCount !== 1 ? 's' : ''}</span>
                        </div>
                        <ArrowsPointingOutIcon
                            onClick={() => setShowHeadersPopup(true)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500 cursor-pointer"
                        />
                    </div>
                </div>

                <div className="flex items-start mb-2">
                    <label className="w-28 text-right text-black mt-1.5">Body</label>
                    <div className="relative w-full ml-2">
                        <textarea
                            className="w-full rounded-md border-gray-300 p-1.5 text-gray-900 shadow-sm focus:ring-2 focus:ring-orange-600 min-h-[80px]"
                            value={body && body !== 'null' ? body : ''}
                            onChange={(evt) => {
                                setAllowDrag(true);
                                setShowSave(true);
                                updateNodeData(id, {
                                    step: {
                                        ...data.step,
                                        apiDestinationStep: {
                                            ...data.step.apiDestinationStep,
                                            body: evt.target.value
                                        }
                                    }
                                })
                            }}
                            onPointerDown={() => setAllowDrag(false)}
                            placeholder="Enter custom body template. Use {{$body}} to insert previous step output. Leave empty to send previous step output."
                        />
                        <div className="text-xs text-left text-gray-500 mt-1 ml-1">
                            If empty, the previous step's output will be sent as the body.
                        </div>
                    </div>
                </div>

                <div className="flex items-center mb-2">
                    <label className="w-28 text-right text-black">Auth Header Name</label>
                    <input
                        className="block ml-2 w-full truncate pr-8 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-orange-600 sm:text-sm sm:leading-6"
                        value={data.step.apiDestinationStep?.authHeaderName || ''}
                        onChange={(evt) => {
                            setAllowDrag(true);
                            setShowSave(true);
                            updateNodeData(id, {
                                step: {
                                    ...data.step,
                                    apiDestinationStep: {
                                        ...data.step.apiDestinationStep,
                                        authHeaderName: evt.target.value
                                    }
                                }
                            })
                        }}
                        onPointerDown={() => setAllowDrag(false)}
                        placeholder="Auth header name..."
                    />
                </div>
                <div className="flex items-center mb-2">
                    <div className="relative group right-0 z-50 flex items-center">
                        <InformationCircleIcon className="absolute  right-0 w-4 h-4 ml-2 text-gray-500 cursor-pointer" />
                        <div className="absolute bottom-full mb-2 hidden group-hover:block w-64 p-2 bg-gray-700 text-white text-sm rounded-lg shadow-lg">
                            New secrets can be created on the profile page
                        </div>
                    </div>

                    <label className="w-28 text-right text-black">Auth Secret Name</label>
                    <select
                        className="flex-1 ml-2 rounded-md border-gray-300 p-1.5 text-gray-900 shadow-sm focus:ring-2 focus:ring-orange-600"
                        value={data.step.apiDestinationStep?.authHeaderSecretName as string || ''}
                        onChange={(evt) => {
                            setAllowDrag(true);
                            setShowSave(true);
                            updateNodeData(id, {
                                step: {
                                    ...data.step,
                                    apiDestinationStep: {
                                        ...data.step.apiDestinationStep,
                                        authHeaderSecretName: evt.target.value
                                    }
                                }
                            })
                        }}
                        onPointerDown={() => setAllowDrag(false)}
                    >
                        <option disabled value="">Select a secret</option>
                        {data?.userSecrets?.map(secret => {
                            return <option value={secret.name} key={secret.id}>{secret.name}</option>
                        })}
                    </select>
                </div>
                <Handle type="target" position={Position.Left} />
            </div>
        </>
    )
}

// Add the new HeadersTablePopup component
interface HeadersTablePopupProps {
    headers: Record<string, string>;
    onSave: (headers: Record<string, string>) => void;
    onClose: () => void;
}

function HeadersTablePopup({ headers, onSave, onClose }: HeadersTablePopupProps) {
    const [headersState, setHeadersState] = useState<Array<{ key: string, value: string, id: string }>>(
        Object.entries(headers).map(([key, value]) => ({ key, value, id: crypto.randomUUID() }))
    );

    const addNewRow = () => {
        setHeadersState([...headersState, { key: '', value: '', id: crypto.randomUUID() }]);
    };

    const removeRow = (id: string) => {
        setHeadersState(headersState.filter(row => row.id !== id));
    };

    const updateRow = (id: string, field: 'key' | 'value', newValue: string) => {
        setHeadersState(headersState.map(row =>
            row.id === id ? { ...row, [field]: newValue } : row
        ));
    };

    const handleSave = () => {
        const newHeaders = headersState.reduce((acc, { key, value }) => {
            if (key.trim()) { // Only save headers with non-empty keys
                acc[key] = value;
            }
            return acc;
        }, {} as Record<string, string>);

        onSave(newHeaders);
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[51]">
            <div className="bg-white p-6 rounded-lg shadow-lg relative max-w-3xl w-full">
                <button className="absolute top-2 right-2 text-gray-500 hover:text-gray-700" onClick={onClose}>
                    <X className="w-5 h-5" />
                </button>
                <h3 className="text-lg font-medium mb-4">HTTP Headers</h3>

                <div className="max-h-[60vh] overflow-y-auto">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr className="bg-gray-50">
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/2">Name</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/2">Value</th>
                                <th className="px-4 py-2 w-12"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {headersState.map((row) => (
                                <tr key={row.id}>
                                    <td className="pr-2 py-2">
                                        <input
                                            type="text"
                                            className="w-full p-1 border rounded focus:ring-2 focus:ring-orange-600"
                                            value={row.key}
                                            onChange={(e) => updateRow(row.id, 'key', e.target.value)}
                                            placeholder="Header name"
                                        />
                                    </td>
                                    <td className="px-2 py-2">
                                        <input
                                            type="text"
                                            className="w-full p-1 border rounded focus:ring-2 focus:ring-orange-600"
                                            value={row.value}
                                            onChange={(e) => updateRow(row.id, 'value', e.target.value)}
                                            placeholder="Header value"
                                        />
                                    </td>
                                    <td className="px-2 py-2">
                                        <button
                                            className="p-1 hover:bg-red-100 rounded"
                                            onClick={() => removeRow(row.id)}
                                        >
                                            <X className="w-4 h-4 text-red-500" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="mt-4 flex justify-between">
                    <button
                        onClick={addNewRow}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center"
                    >
                        <PlusCircleIcon className="w-4 h-4 mr-2" /> Add Header
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700"
                    >
                        Save Headers
                    </button>
                </div>
            </div>
        </div>
    );
}

function JSONTransformationNode({ id, data }: NodeProps<Node<{
    step: Step,
    workflowID: string,
    setShowTemplateCopyIconPulse: (value: boolean) => void,
    setShowSignUpPopup: (value: boolean) => void
}>>) {
    const { updateNodeData } = useReactFlow();
    const user = useAppSelector((state) => state.user);
    const dispatch = useDispatch();
    const [showSave, setShowSave] = useState(false);
    const [showJMESPathPopup, setShowJMESPathPopup] = useState(false);
    const [isGeneratingJMESPath, setIsGeneratingJMESPath] = useState(false);
    const [showJMESPathEditor, setShowJMESPathEditor] = useState(false);

    const generateJMESPath = async (prompt: string) => {
        if (!user.credentials?.accessToken) {
            dispatch(setNotification({
                type: 'error',
                message: 'Not authenticated'
            }));
            return;
        }

        setIsGeneratingJMESPath(true);
        try {
            const result = await getJmesPathSuggestion(user.credentials.accessToken, prompt)

            setShowSave(true);
            updateNodeData(id, {
                step: {
                    ...data.step,
                    jsonTransformationStep: {
                        ...data.step.jsonTransformationStep,
                        jmespath: result
                    }
                }
            });

            return result;
        } catch (error) {
            dispatch(setNotification({
                type: 'error',
                message: `Failed to generate JMESPath: ${error}`
            }));
            return null;
        } finally {
            setIsGeneratingJMESPath(false);
        }
    };

    return (
        <>
            {showJMESPathPopup && (
                <JMESPathAssistantPopup
                    onClose={() => setShowJMESPathPopup(false)}
                    onSubmit={async (prompt) => {
                        const jmespath = await generateJMESPath(prompt);
                        if (jmespath) {
                            setShowJMESPathPopup(false);
                        }
                    }}
                    isGenerating={isGeneratingJMESPath}
                />
            )}

            {showJMESPathEditor && (
                <PopupInput
                    onClose={() => setShowJMESPathEditor(false)}
                    setText={(value) => {
                        setShowSave(true);
                        updateNodeData(id, {
                            step: {
                                ...data.step,
                                jsonTransformationStep: {
                                    ...data.step.jsonTransformationStep,
                                    jmespath: value
                                }
                            }
                        });
                    }}
                    text={data.step.jsonTransformationStep?.jmespath || ''}
                    placeholder="Enter JMESPath expression..."
                />
            )}

            <div className="space-y-3 w-72">
                <div className="text-black flex items-center justify-center">
                    JSON Transform
                    {showSave && <FileIcon
                        onClick={() => {
                            if (data.workflowID.startsWith("wf_template") && user?.credentials) {
                                dispatch(setNotification({
                                    type: 'info',
                                    message: 'This workflow is a template. Click copy to edit it.'
                                }));
                                data.setShowTemplateCopyIconPulse(true);
                                return;
                            }

                            if (!user?.credentials) {
                                data.setShowSignUpPopup(true);
                                return;
                            }

                            updateStep(user.credentials!.accessToken, data.workflowID, data.step.id!, data.step)
                                .then(() => {
                                    dispatch(setNotification({
                                        type: 'success',
                                        message: 'Step saved successfully'
                                    }));
                                    setShowSave(false);
                                })
                                .catch((reason) => {
                                    console.error('Failed to save step', reason);
                                    dispatch(setNotification({
                                        type: 'error',
                                        message: 'Failed to save step' + reason
                                    }));
                                });
                        }}
                        className="w-4 h-4 cursor-pointer ml-2"
                    />}

                    <StepOutputButton output={data.step.lastOutput} title="Transform step output" subtitle={data.step.jsonTransformationStep?.jmespath || 'JMESPath result'} workflowID={data.workflowID} stepID={data.step.id} />
                </div>

                <div className="flex items-center mb-2">
                    <label className="mr-2 text-black">JMESPath</label>
                    <div className="relative w-full max-w-xs">
                        <input
                            type="text"
                            placeholder="jmespath..."
                            onChange={(evt) => {
                                setShowSave(true);
                                updateNodeData(id, {
                                    step: {
                                        ...data.step,
                                        jsonTransformationStep: {
                                            ...data.step.jsonTransformationStep,
                                            jmespath: evt.target.value
                                        }
                                    }
                                });
                            }}
                            value={data.step.jsonTransformationStep?.jmespath || ''}
                            className="block w-full pr-16 truncate rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-orange-600 sm:text-sm sm:leading-6"
                        />
                        <div className="absolute right-0 top-0 h-full flex items-center pr-1">
                            <ArrowsPointingOutIcon
                                onClick={() => setShowJMESPathEditor(true)}
                                className="w-5 h-5 text-gray-500 cursor-pointer mr-1.5"
                                title="Edit in larger view"
                            />
                            <SparklesIcon
                                onClick={() => setShowJMESPathPopup(true)}
                                className="w-5 h-5 text-orange-500 cursor-pointer"
                                title="Generate with AI"
                            />
                        </div>
                    </div>
                </div>

                <Handle type="target" position={Position.Left} />
                <Handle type="source" position={Position.Right} />
            </div>
        </>
    );
}


function RedditActionNode({ id, data }: NodeProps<Node<{
    step: Step,
    workflowID: string,
    setShowTemplateCopyIconPulse: (value: boolean) => void,
    setShowSignUpPopup: (value: boolean) => void
}>>) {
    const { updateNodeData } = useReactFlow();
    const user = useAppSelector((state) => state.user);
    const dispatch = useDispatch();
    const [showSave, setShowSave] = useState(false);
    const [showPopupInput, setShowPopupInput] = useState(false);

    return (
        <div className="space-y-3 w-72">
            <div className="text-black flex items-center justify-center">
                Reddit Post Step
                {showSave && (
                    <FileIcon
                        onClick={() => {
                            if (data.workflowID.startsWith("wf_template") && user?.credentials) {
                                dispatch(setNotification({
                                    type: 'info',
                                    message: 'This workflow is a template. Click copy to edit it.'
                                }));
                                data.setShowTemplateCopyIconPulse(true);
                                return;
                            }

                            if (!user?.credentials) {
                                data.setShowSignUpPopup(true);
                                return;
                            }

                            updateStep(user.credentials!.accessToken, data.workflowID, data.step.id!, data.step)
                                .then(() => {
                                    dispatch(setNotification({
                                        type: 'success',
                                        message: 'Step saved successfully'
                                    }));
                                    setShowSave(false);
                                })
                                .catch((reason) => {
                                    dispatch(setNotification({
                                        type: 'error',
                                        message: 'Failed to save step: ' + reason
                                    }));
                                });
                        }}
                        className="w-4 h-4 cursor-pointer ml-2"
                    />
                )}
                    <StepOutputButton output={data.step.lastOutput} title="Reddit step output" subtitle={data.step.redditStep?.subreddit ? `r/${data.step.redditStep.subreddit}` : 'Reddit action result'} workflowID={data.workflowID} stepID={data.step.id} />
            </div>
            <div className="flex items-center gap-2">
                <label className="w-24 text-right text-black">Subreddit r/</label>
                <input
                    className="flex-1 rounded-md border-gray-300 p-1.5 text-gray-900 shadow-sm focus:ring-2 focus:ring-orange-600"
                    value={data.step.redditStep?.subreddit || ''}
                    onChange={(evt) => {
                        setShowSave(true);
                        updateNodeData(id, {
                            step: {
                                ...data.step,
                                redditStep: {
                                    ...data.step.redditStep,
                                    subreddit: evt.target.value
                                }
                            }
                        });
                    }}
                    placeholder="e.g. wallstreetbets"
                />
            </div>
            <div className="flex items-center gap-2">
                <label className="w-24 text-right text-black mr-4">Title</label>
                <div className="relative group z-50 flex items-center">
                    <InformationCircleIcon className="absolute right-0 w-4 h-4 ml-2 text-gray-500 cursor-pointer" />
                    <div className="absolute bottom-full mb-2 hidden group-hover:block w-64 p-2 bg-gray-700 text-white text-sm rounded-lg shadow-lg" dangerouslySetInnerHTML={{ __html: `Either an explicit title or a jmespath wrapped in {{}}. E.x. the previous step outputs this JSON {'title': 'My Title', 'body': "some text"} then the jmespath for this step could be {{title}}` }} />
                </div>
                <input
                    className="flex-1 rounded-md border-gray-300 p-1.5 text-gray-900 focus:ring-2 focus:ring-orange-600"
                    value={data.step.redditStep?.title || ''}
                    onChange={(evt) => {
                        setShowSave(true);
                        updateNodeData(id, {
                            step: {
                                ...data.step,
                                redditStep: {
                                    ...data.step.redditStep,
                                    title: evt.target.value
                                }
                            }
                        });
                    }}
                    placeholder="Post title"
                />
            </div>
            <div className="flex items-center gap-2">
                <label className="w-24 text-right text-black mr-4">Body</label>
                <div className="relative group z-50 flex items-center">
                    <InformationCircleIcon className="absolute right-0 w-4 h-4 ml-2 text-gray-500 cursor-pointer" />
                    <div className="absolute bottom-full mb-2 hidden group-hover:block w-64 p-2 bg-gray-700 text-white text-sm rounded-lg shadow-lg" dangerouslySetInnerHTML={{ __html: `Either an explicit body or a jmespath wrapped in {{}}. E.x. the previous step outputs {'title': 'My Title', 'body': "some text"} and the jmespath is {{body}}. If left empty the raw output of the last step will be used.` }} />
                </div>
                {showPopupInput && (
                    <PopupInput
                        onClose={() => setShowPopupInput(false)}
                        setText={(value: string) => {
                            setShowSave(true);
                            updateNodeData(id, {
                                step: {
                                    ...data.step,
                                    redditStep: {
                                        ...data.step.redditStep,
                                        content: value
                                    }
                                }
                            });
                        }}
                        text={data.step.redditStep?.content || ''}
                    />
                )}
                <div className="relative w-full max-w-xs">
                    <input
                        type="text"
                        placeholder="Enter text"
                        onChange={(evt) => {
                            setShowSave(true);
                            updateNodeData(id, { step: { ...data.step, redditStep: { ...data.step.redditStep, content: evt.target.value } } })
                        }}
                        value={data.step.redditStep?.content || ''}
                        className="block w-full pr-8 truncate rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-orange-600 sm:text-sm sm:leading-6"
                    />
                    <ArrowsPointingOutIcon onClick={() => {
                        setShowPopupInput(true)
                    }} className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500 cursor-pointer" />
                </div>
            </div>
            <Handle type="target" position={Position.Left} />
        </div>
    );
}

function ScheduleNode({ id, data }: NodeProps<Node<{
    step: Step,
    workflowID: string,
    setShowSignUpPopup: (value: boolean) => void
}>>) {
    const { updateNodeData } = useReactFlow();
    const dispatch = useDispatch();
    const [showSave, setShowSave] = useState(false);
    const user = useAppSelector((state) => state.user);
    const [allowDrag, setAllowDrag] = useState(true);
    return (
        <div className={`space-y-3 w-72 ${!allowDrag ? 'nodrag' : ''}`}>
            <div className="text-black flex items-center justify-center">
                Schedule Step
                {showSave && (
                    <FileIcon
                        onClick={() => {
                            if (data.workflowID.startsWith("wf_template") && user?.credentials) {
                                dispatch(setNotification({
                                    type: 'info',
                                    message: 'This workflow is a template. Click copy to edit it.'
                                }));
                                return;
                            }

                            if (!user?.credentials) {
                                data.setShowSignUpPopup(true);
                                return;
                            }

                            updateStep(user.credentials!.accessToken, data.workflowID, data.step.id!, data.step)
                                .then(() => {
                                    dispatch(setNotification({
                                        type: 'success',
                                        message: 'Step saved successfully'
                                    }));
                                    setShowSave(false);
                                })
                                .catch((reason) => {
                                    dispatch(setNotification({
                                        type: 'error',
                                        message: 'Failed to save step: ' + reason
                                    }));
                                });
                        }}
                        className="w-4 h-4 cursor-pointer ml-2"
                    />
                )}
                <StepOutputButton output={data.step.lastOutput} title="Schedule step output" subtitle={data.step.scheduleStep?.schedule || 'Scheduled execution result'} workflowID={data.workflowID} stepID={data.step.id} />
            </div>
            <div className="flex items-center gap-2">
                <label className="w-16 text-right text-black">Schedule</label>
                <select
                    className="flex-1 w-full overflow-clip text-ellipsis rounded-md border-gray-300 p-1.5 text-gray-900 shadow-sm focus:ring-2 focus:ring-orange-600"
                    onChange={(evt) => {
                        setAllowDrag(true);
                        updateNodeData(id, { step: { ...data.step, scheduleStep: { ...data.step.scheduleStep, schedule: evt.target.value } } });
                        setShowSave(true);
                    }}
                    onPointerDown={() => setAllowDrag(false)}
                    defaultValue={data.step.scheduleStep?.schedule}
                >
                    <option value="* * * * *">Every Minute</option>
                    <option value="0/15 * * * *">Every 15 Minutes</option>
                    <option value="0 * * * *">Hourly</option>
                    <option value="0 0 * * *">Daily</option>
                </select>
            </div>

            <Handle type="source" position={Position.Right} />
        </div>
    )
}
